import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { cabinetApi, type ClusterInput, type HsiDriftCollection } from '../api'
import { useOrg } from '../org'
import { useRailSlot } from '../layout/railSlot'
import { useViewCtx } from '../context/ViewContext'
import { ContextBlock } from '../layout/ContextBlock'
import { OverlaysBlock } from '../layout/OverlaysBlock'
import { MapCanvas, type MapCanvasHandle } from '../map/MapCanvas'
import { useMapView } from '../map/mapView'
import { RegionForbiddenPlate } from '../species/RegionForbiddenPlate'
import { viewToOpen } from '../species/regionHome'
import { dayRange, hasHistory } from '../domain/limits'
import { useDomainLimits } from '../domain/useDomainLimits'
import { useRegionScope } from '../species/useRegionScope'
import { regionAt } from '../species/regionFilter'
import { useVesselFocus } from '../map/useVesselFocus'
import { useVesselTrack } from '../map/useVesselTrack'
import { useVesselForecast } from '../map/useVesselForecast'
import { forecastHint } from './forecastHint'
import { trackHint } from '../map/vesselOps'
import { EezCard } from '../map/EezCard'
import { useEezCard } from '../map/useEezCard'
import { useDistrictsLayer } from '../map/useDistrictsLayer'
import { useSubstrateLayer } from '../map/useSubstrateLayer'
import { useNavwarnLayer } from '../map/useNavwarnLayer'
import { substrateAt } from '../map/substrateLayer'
import { useBansLayer } from '../map/useBansLayer'
import { useEezLayer } from '../map/useEezLayer'
import { useIsobathTool } from '../map/useIsobathTool'
import { useTilePalette } from '../map/useTilePalette'
import { ExportViewButton } from '../map/ExportViewButton'
import { ShareLinkButton } from '../map/ShareLinkButton'
import { useExportInfo } from '../map/useExportInfo'
import { useFleetLayer } from '../map/useFleetLayer'
import { useOverlays } from '../map/useOverlays'
import { LoadDataButton } from '../map/LoadDataButton'
import { ManifestModal } from '../map/ManifestModal'
import { useLoadData } from '../map/useLoadData'
import { bathymetryTileUrl, probabilityTileUrl, sharedTileUrl } from '../map/tileUrl'
import { type SeriesFrame } from '../map/seriesCatalog'
import { composeSeries } from '../map/seriesCompose'
import { framesForCabinet } from '../map/seriesCabinet'
import { hydrologyOf } from '../hydrology'
import { frameOverride, scaleLayerOf, type ToolsView } from '../map/seriesOverride'
import { waitFor } from '../map/toolPending'
import { probeFor, renderRequestFor } from './seriesReadiness'
import type { FrameVerdict } from '../map/seriesPlan'
import type { SeriesRunner } from '../map/exportSeries'
import { ModelAccordion, MODELS, type ModelKind } from './ModelPicker'
import { VesselCard } from './VesselCard'
import { VesselTrackLegend } from './VesselTrackLegend'
import { ClusterPeek } from './ClusterPeek'
import {
  activeCard,
  clusterPeek,
  clustersWithoutTrack,
  driftClickTarget,
  refreshedPick,
  type MapPick,
} from './cardPick'
import { DriftControls } from './DriftControls'
import { DriftLegend } from './DriftLegend'
import { CollapsibleLegend } from '../map/CollapsibleLegend'
import { UpwellingLegend } from './UpwellingLegend'
import { UpwellingControl } from './UpwellingControl'
import {
  clampSelection, depthApplicable as upwDepthApplicable, mechanismsFrom,
  nearestMechDepth, snapDepthOnEnter,
} from './upwellingSelection'
import { useUpwellingIso } from '../map/useUpwellingIso'
import { driftStatus } from './driftStatus'
import { ClusterForm } from './ClusterForm'
import { TrackPointCard } from './TrackPointCard'
import { DayTimeline } from './DayTimeline'
import {
  sliceDriftCollection,
  pickClusterDrift,
  humanDate,
  dateFromOffset,
  dayOffset,
} from './driftDay'
import { datesToRender } from './videoDates'
import { renderDates } from './videoPrepare'
import {
  framePlan,
  recordWindow,
  windowLabelRange,
  type RecordWindow,
} from '../map/videoTimeline'
import { sliceHour } from '../map/exportTimer'
import {
  fleetCoverageNotes,
  frameTimeMs,
  mergeFleetHistory,
  sliceFleetAt,
  type FleetHistory,
} from '../map/fleetHistory'
import { backgroundDecision, backgroundLayerFor, backgroundTileUrl, type BackgroundLayer } from './background'
import { clampHsi, hsiKindsFrom, visibleKinds, NN } from './hsiSelection'
import { HsiControl } from './HsiControl'
import { marksCollection } from './nnMarksLayer'
import { SwitchRow } from './SwitchRow'
import { pollTilesReady } from './resolveTiles'
import { probabilityAutoShow, shouldRepointProbabilityLayer } from './probabilityLayer'
import { arrowStepDeg, arrowScale, arrowsNotice, type ArrowsState } from './arrows'
// Инструмент температуры (изотермы + градиенты) — единый общий хук + подпись,
// состояние в ViewContext (блок «Наложения»), фикс 5–16 °C над любым слоем.
import { useTemperatureTools } from '../map/useTemperatureTools'
import { useSiteBrand, useT } from '../i18n'
import { useWaveIso } from '../map/useWaveIso'
import { MapLegendPanel } from '../maps/MapLegendPanel'
import { ScalePanel } from '../maps/ScalePanel'
import { useLayerSpec } from '../maps/useLayerPalette'
import { withSectionLayers } from '../map/overlayLayers'
import { HsiDriftControl } from './HsiDriftControl'
import { waitMapIdle } from '../map/mapIdle'
import { waitTilesReady } from '../map/tilesReady'
import { layerStateOf, type LayerShowState } from '../map/exportReady'
import type { ExportAnimation } from '../map/exportVideo'
import {
  COMET_CYCLE_MS,
  buildCometFrame,
  prepareTrails,
  type CometTrail,
} from './hsiCometFrame'
import { hsiDriftShapes } from './hsiDriftDay'
import { cometMode } from './hsiCometMode'
import {
  clusterCreateAllowed,
  driftForSection,
  driftFetchedIn,
  schoolsShownIn,
  showsDayTimeline, hsiDepthOff, allowedSection, sectionAllowed } from './sectionRules'
import {
  HISTORY_POLL_ATTEMPTS,
  HISTORY_POLL_MS,
  STATUS_UNKNOWN,
  historyProgress,
  shouldFillHistory,
  type HistoryProgress,
} from './historyDownload'
import {
  type HsiRecomputeProgress,
  currentsSettled,
  runHsiRecompute,
  shouldRecomputeHsi,
} from './hsiRecompute'
import { bustedTileUrl } from '../map/readiness'
import { isobathZoomStep } from '../map/isobaths'
import {
  loadMemory,
  saveMemory,
  nextDraft,
  clusterRadius,
  clusterOpacity,
  CLUSTER_COLOR,
  visibleClusters,
} from './clusterDraft'
import { applySchoolsVisibility } from './schoolsVisibility'
import { SchoolsControl } from './SchoolsControl'
import { EarthquakeCard } from './EarthquakeCard'
import { PointCard } from '../map/PointCard'
import { usePointCard } from '../map/usePointCard'
import { useQuakeCard } from '../map/useQuakeCard'
import { saveViewState } from '../settings/saveViewState'
import { backendScopeFor, serializeFleet } from './fleetSegments'
import { Icon } from '../brand/Icon'
import { DEFAULT_MODELS_STATE, parseModelsState, applyModelsState, modelsSectionSeed } from './modelsUrlState'
import { useDebouncedValue } from './useDebouncedValue'
import { MapPanel } from '../map/MapPanel'

const today = () => new Date().toISOString().slice(0, 10)

/** Снесённая точка под курсором/закреплённая: то, что показывает TrackPointCard. */
interface DriftPick {
  lng: number
  lat: number
  source: string
  frozen: boolean
  /** Свойства фичи целиком — из них карточка берёт градацию улова. */
  props: Record<string, unknown>
}

/** Пауза, после которой значение ползунка окна считается выбранным (мс). */
const WINDOW_SETTLE_MS = 250

/** Потолок ожидания инструментов кадра серии. Больше — и одна недогруженная
 *  сетка держала бы всю серию; меньше — и холодная сетка не успевала бы. */
const SERIES_TOOL_TIMEOUT_MS = 15_000
/** Ожидание отрисовки после того, как плитки уже приехали. */
const SERIES_IDLE_MS = 1500
/** Потолок ожидания САМИХ плиток кадра.
 *
 *  Отдельный и большой, потому что плитки батиметрии рисуются лениво из GEBCO
 *  по первому запросу: замер 18.08.2026 — двадцать штук кадра за 17 секунд.
 *  Прежние полторы секунды клали в архив полосу шириной ровно в один тайл. */
const SERIES_TILES_TIMEOUT_MS = 60_000
/** Потолок ожидания рендера одного кадра: полторы секунды на попытку, то есть
 *  полминуты на кадр. По минуте на кадр растянуло бы серию до пяти минут. */
const SERIES_RENDER_ATTEMPTS = 20

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

/**
 * Системная настройка «меньше движения», реактивно.
 *
 * Разовое чтение `matchMedia(...).matches` внутри эффекта отвечает на вопрос
 * «как было в момент запуска эффекта» — а настройку переключают на живой
 * странице. Подписка на `change` возвращает нас в React-мир: смена настройки
 * = ререндер, и все производные от неё решения (в т.ч. `cometsRunning`)
 * пересчитываются одинаково и для UI, и для карты.
 */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => window.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false,
  )
  useEffect(() => {
    const mq = window.matchMedia?.(REDUCED_MOTION_QUERY)
    if (!mq) return
    setReduced(mq.matches)          // настройка могла смениться до подписки
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

export function ModelsPage() {
  const qc = useQueryClient()
  const t = useT()
  const mapRef = useRef<MapCanvasHandle>(null)
  // Палитра тайлов домена; missing — наша гамма и прежний адрес тайла.
  const palette = useTilePalette()

  // Снимок URL на момент монтирования — восстанавливаем настройки раздела из ссылки
  // (рефреш/шаринг). Читаем один раз: дальнейшую синхронизацию ведёт effect ниже.
  const [searchParams, setSearchParams] = useSearchParams()
  const bootRef = useRef<ReturnType<typeof parseModelsState>>()
  if (!bootRef.current) bootRef.current = parseModelsState(searchParams)
  const boot = bootRef.current
  // Сид раздела (снимок URL на монтировании): холодный вход без вида/флота открывает
  // течения на сайре + Ин.флоте. Явные ключи из ссылки/шаринга не трогаем.
  const seedRef = useRef<ReturnType<typeof modelsSectionSeed>>()
  const brandSpecies = useSiteBrand()?.default_species ?? null
  if (!seedRef.current) seedRef.current = modelsSectionSeed(searchParams, brandSpecies)

  // Рамка и окно дат домена (решение владельца 20.10.2026): без истории
  // «Дрейфы» сняты, ролик идёт только по разрешённым суткам.
  const domainLimits = useDomainLimits()
  const history = hasHistory(domainLimits)
  const [modelState, setModel] = useState<ModelKind>(boot.model ?? DEFAULT_MODELS_STATE.model)
  // Раздел решается на ЧТЕНИИ: на домене без истории `?model=currents` и
  // умолчание («Дрейфы») сразу дают «Комфортную среду», без кадра с запросом
  // сноса, на который сервер ответил бы отказом.
  const model = allowedSection(modelState, history)
  // Дата·глубина·вид — из общего контекста (блок «Контекст» над разделами). Живут
  // над маршрутами и переживают переход Карты↔Аналитика; сериализуются в URL этой
  // же страницей через applyModelsState (ключи date/depth/species — её собственные).
  // setDepth/setSpecies живут в блоке «Контекст»; здесь wanted только значения
  // (+ setDate для ползунка дней DayTimeline). Флот/оверлеи — тоже из общего стора
  // (блок «Наложения»), рисуются на карте обоих разделов.
  // Дата/глубина/вид + флот/оверлеи + инструмент температуры (isotherm/fronts/
  // frontsUnit) — из общего стора; здесь только значения (setDate для ползунка дней).
  const {
    date,
    setDate,
    depth,
    setDepth,
    species,
    setSpecies,
    setDepthApplicable,
    setDepthNote,
    fleet,
    toggleFleet,
    overlays,
    showUncategorized,
    flags,
    setFlagCounts,
    focusVessel,
    isobathStep,
    waveOverlay,
    waveStep,
    isotherm,
    isothermSurface,
    isothermSpread,
    fronts,
    frontsUnit,
  } = useViewCtx()
  // «Траектории по HSI»: отдельный слой обратного сноса HSI-поля. Снос косяков
  // (drift/clusters) не трогаем — это независимый слой поверх той же карты.
  const [hsiDrift, setHsiDrift] = useState<boolean>(boot.hsiDrift ?? DEFAULT_MODELS_STATE.hsiDrift)
  const [hsiDriftData, setHsiDriftData] = useState<HsiDriftCollection | null>(null)
  // Косяки, отмеченные вручную. Включены по умолчанию: их ставит сам
  // промысловик, и прятать его работу без спроса нельзя.
  const [schools, setSchools] = useState<boolean>(boot.schools ?? DEFAULT_MODELS_STATE.schools)
  const [nnMarksOn, setNnMarksOn] = useState<boolean>(boot.nnMarks ?? DEFAULT_MODELS_STATE.nnMarks)
  // Исторический вылов в «Комфортной среде» — только кабинету с признаком
  // `historical_catch` (админка). Без признака ручка сноса вылов не отдаёт.
  const catchAllowed = useOrg().activeOrg?.services?.historical_catch !== false
  const [catchOn, setCatchOn] = useState<boolean>(boot.catchShown ?? DEFAULT_MODELS_STATE.catchShown)
  const catchShown = catchOn && catchAllowed
  // Прогнозный ход выбранного судна: считается в браузере по курсу и скорости
  // последнего фикса (см. map/vesselForecast.ts).
  const [vesselForecast, setVesselForecast] = useState<boolean>(
    boot.vesselForecast ?? DEFAULT_MODELS_STATE.vesselForecast,
  )
  // Семья механизмов апвеллинга: чем закрашено поле и что наложено линиями.
  // Каталог механизмов приезжает из `/cabinet/layers` (group === 'upwelling'),
  // поэтому приведение выбора (`clampSelection`) делается ПОСЛЕ его приезда —
  // до того судить о том, что бывает, нечем.
  // Подраздел «Комфортной среды»: нынешний индекс или карта сети. Приведение
  // (`clampHsi`) — ПОСЛЕ приезда каталога, по той же причине, что у механизмов.
  const [hsiKind, setHsiKind] = useState<string>(boot.hsi ?? DEFAULT_MODELS_STATE.hsi)
  const [upw, setUpw] = useState<string>(boot.upw ?? DEFAULT_MODELS_STATE.upw)
  const [upwIso, setUpwIso] = useState<string[]>(boot.upwIso ?? DEFAULT_MODELS_STATE.upwIso)
  const [upwZero, setUpwZero] = useState<boolean>(boot.upwZero ?? DEFAULT_MODELS_STATE.upwZero)

  // Анимация усиков кометами. В URL НЕ пишем: режим эфемерный, ссылкой не делятся.
  const [hsiAnim, setHsiAnim] = useState(false)
  // Системное «меньше движения» — в состоянии, а не разовым чтением внутри эффекта:
  // настройку меняют на лету (macOS/Windows делают это без перезагрузки вкладки), и
  // без подписки карта осталась бы в режиме, от которого пользователь только что
  // отказался (или наоборот — продолжила бы двигаться).
  const reduced = usePrefersReducedMotion()
  const cometTrailsRef = useRef<CometTrail[]>([])
  // Идёт запись ролика: свободный такт анимации замолкает, фазы задаёт запись
  // (см. exportVideo — только так шов цикла получается нулевым). Ref, а не
  // состояние: такт читает флаг внутри requestAnimationFrame, и лишний
  // ререндер здесь никому не нужен.
  const recordingRef = useRef(false)
  // Тот же признак СОСТОЯНИЕМ — им глушится слой живого флота (`paused`).
  // Ref для этого не годится: хук обязан перезапустить эффект, когда запись
  // кончилась, и сам перечитать живые позиции. Два носителя одного флага
  // выглядят лишними ровно до первого ререндера посреди записи.
  const [recording, setRecording] = useState(false)
  // История позиций флота за окно записи: забирается один раз в `begin`,
  // режется по моменту кадра (см. map/fleetHistory.ts).
  const fleetHistoryRef = useRef<FleetHistory | null>(null)
  // Итог по флоту — ОТДЕЛЬНО от самой истории и переживает `end()`: подпись
  // охвата спрашивается уже после того, как запись вернула экран на место.
  const fleetMetaRef = useRef<FleetHistory['meta'] | null>(null)
  // Расчёт HSI, запускаемый цепочкой кнопки «Обновить» (см. maybeRecomputeHsi):
  // своя полоска рядом с основной, кнопку не блокирует.
  const [hsiProgress, setHsiProgress] = useState<HsiRecomputeProgress | null>(null)
  // Докачка истории течений: недостающие сутки заказывает КНОПКА (см.
  // shouldFillHistory), полоска доводит работу до конца на глазах у пользователя.
  const [historyProg, setHistoryProg] = useState<HistoryProgress | null>(null)
  const historyBusyRef = useRef(false)
  // Свой замок «одна докачка на клик». Отдельный от hsiArmedRef, потому что
  // взводится он ИНАЧЕ: пересчёт HSI заказывает и включение тумблера, докачка —
  // только кнопка. Смешав их, мы вернули бы закачку течений от одного тумблера.
  const historyArmedRef = useRef(false)
  // Замок «одна попытка на явное действие»: взводится кликом по «Обновить» ИЛИ
  // включением тумблера «Траектории HSI», снимается первой же проверкой. Без него
  // эффект усиков (срабатывает на КАЖДЫЙ сдвиг карты) гонял бы минуты тяжёлого
  // пересчёта, а неудачный расчёт зациклил бы цепочку.
  const hsiArmedRef = useRef(false)
  const hsiBusyRef = useRef(false)
  // Нонс перезапроса усиков после успешного расчёта HSI.
  const [hsiNonce, setHsiNonce] = useState(0)
  // Глубина для перерисовки слоя вероятности: ползунок при протяжке шлёт значение
  // на каждый уровень, а probability-тайлы рендерятся лениво из pf — дебаунсим,
  // чтобы протяжка не порождала шквал ленивых рендеров (маркер/подпись мгновенны).
  const settledDepth = useDebouncedValue(depth, WINDOW_SETTLE_MS)
  // Дата для запроса дрейфа: от неё сервер отсчитывает окно свежести. Ползунок
  // дней двигает дату шагами, и без отстоя каждый шаг бил бы в сеть.
  const settledDriftDate = useDebouncedValue(date, WINDOW_SETTLE_MS)
  // Отсчёт по клику. Вид передаём: раздел «Модели» и есть про оценку, и строка
  // пригодности здесь — главная. Глубина — отстоявшаяся: карточка одна на клик,
  // а протяжка ползунка иначе била бы в сеть на каждый уровень.
  const pointCard = usePointCard({ date, depth: settledDepth, species })
  const [newPoint, setNewPoint] = useState<{ lng: number; lat: number; x: number; y: number } | null>(null)
  const [editPoint, setEditPoint] = useState<{ id: number; lng: number; lat: number; x: number; y: number } | null>(null)
  // Состояние слоя стрелок — показывается под слайдером (см. arrowsNotice).
  const [arrowsState, setArrowsState] = useState<ArrowsState>({ kind: 'ok' })
  // Изобаты не загрузились — говорим об этом, а не показываем голое поле.
  const [isobathsFailed, setIsobathsFailed] = useState(false)
  // Объект под курсором (читалка) и закреплённый кликом. Что из них показать —
  // решает activeCard; закреплённые карточки разных слоёв висят одновременно.
  const [vesselHover, setVesselHover] = useState<MapPick | null>(null)
  const [vesselPick, setVesselPick] = useState<MapPick | null>(null)
  const [clusterHover, setClusterHover] = useState<MapPick | null>(null)
  // Снесённая точка (косяк/факт вылова): показываем её координаты — перенести в навигатор.
  const [driftHover, setDriftHover] = useState<DriftPick | null>(null)
  const [driftPick, setDriftPick] = useState<DriftPick | null>(null)

  // Синхронизация настроек раздела в URL. applyModelsState копирует prev и трогает
  // ТОЛЬКО свои ключи, сохраняя center/zoom карты (ими владеет useMapView).
  // ВАЖНО: `prev` в setSearchParams — снимок из замыкания рендера, НЕ живой URL
  // (react-router), поэтому структурной защиты от второго писателя нет. Безопасно
  // ровно пока никакой ОДИН commit не меняет одновременно поле раздела И вид карты:
  // MapCanvas читает center/zoom только при монтировании и не двигает карту в ответ
  // на ре-рендер → moveend от смены настроек не возникает, два писателя не срабатывают
  // в одном commit. Если появится «сбросить вид»/«применить сохранённый вид» (setModel
  // + рецентр разом) — объединить писателей в один (иначе один из них молча потеряется).
  useEffect(() => {
    setSearchParams(
      prev => applyModelsState(prev, {
        model, species, depth, date, fleet,
        overlays, waves: waveOverlay, showUncat: showUncategorized, waveStep, flags,
        isobath: isobathStep, isotherm, isothermSurface, isothermSpread,
        fronts, frontsUnit, hsiDrift, schools, vesselForecast,
        hsi: hsiKind, upw, upwIso, upwZero, nnMarks: nnMarksOn, catchShown: catchOn,
      }),
      { replace: true },
    )
    // Список обязан повторять ВСЁ, что уходит в applyModelsState: значение без
    // зависимости читается из замыкания прошлого рендера и в ссылку не уезжает,
    // пока не дёрнешь что-нибудь соседнее. Так молчали `isobathStep` и `flags`
    // (замечено владельцем 14.08.2026: изобаты включаются, адрес не меняется).
    // Сторож совпадения списков — models/urlSyncDeps.test.ts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, species, depth, date, fleet, overlays, waveOverlay, showUncategorized, waveStep, flags, isobathStep, isotherm, isothermSurface, isothermSpread, fronts, frontsUnit, hsiDrift, schools, vesselForecast, hsiKind, upw, upwIso, upwZero, nnMarksOn, catchOn])

  // Сид дефолта раздела — один раз при монтировании. Ставим сайру/Ин.флот только если
  // общий контекст пуст (вид не выбран, флот выключен): ссылку/шаринг и уже выбранный
  // в «Картах» вид не переопределяем. Дату не трогаем (остаётся «сегодня»/последняя).
  const seededRef = useRef(false)
  useEffect(() => {
    if (seededRef.current) return
    seededRef.current = true
    const seed = seedRef.current
    if (seed?.species != null && species == null) setSpecies(seed.species)
    if (seed?.fleet?.length && fleet.length === 0) seed.fleet.forEach(toggleFleet)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const railSlot = useRailSlot()
  const mapView = useMapView()
  // Районы, разрешённые доменом и правами организации: карта открывается рамкой
  // первого из них, пустое пересечение говорит плашкой, а не пустотой.
  const regionScope = useRegionScope()
  const openWith = viewToOpen(
    { lng: mapView.center?.[0] ?? null, lat: mapView.center?.[1] ?? null,
      zoom: mapView.zoom ?? null },
    regionScope.home,
    regionScope.known,
  )
  // Активная организация: смена кабинета меняет scope=own — перезапрашиваем суда.
  const { activeOrgId } = useOrg()
  // Каталог слоёв — единственный источник прав кабинета (см. hydrology.ts).
  const { data: layerCatalog } = useQuery({
    queryKey: ['cabinet', 'layers'],
    queryFn: () => cabinetApi.layers(),
    staleTime: 5 * 60_000,
  })

  // Каталог семьи приезжает из общего ответа `/cabinet/layers` (тот же ключ
  // запроса, что у палитр, — лишнего похода в сеть нет).
  const upwCatalog = useMemo(
    () => mechanismsFrom(layerCatalog?.layers ?? []),
    [layerCatalog],
  )
  // Подразделы «Комфортной среды» — из того же ответа каталога.
  const hsiCatalog = useMemo(
    () => hsiKindsFrom(layerCatalog?.layers ?? []),
    [layerCatalog],
  )
  // Видны только те, у кого модель для ЭТОГО вида есть (решение владельца
  // 05.09.2026). Показанный у вида без модели подраздел дал бы пустую карту с
  // кнопкой «Загрузить», которая её не исправит, — то есть немой отказ.
  const hsiVisible = useMemo(
    () => visibleKinds(hsiCatalog, species),
    [hsiCatalog, species],
  )
  const hsiSel = useMemo(
    () => clampHsi(hsiKind, hsiCatalog, species),
    [hsiKind, hsiCatalog, species],
  )
  // Приведение — ПОСЛЕ приезда каталога: до него судить о том, что бывает,
  // нечем, и ссылка `?upw=upw_eddy` открылась бы на эталоне.
  const upwSel = useMemo(
    () => clampSelection({ raster: upw, iso: upwIso, zero: upwZero }, upwCatalog),
    [upw, upwIso, upwZero, upwCatalog],
  )
  // Двадцать мест нейрокарты. Спрашиваем ТОЛЬКО когда подраздел выбран: у
  // прочих видов и подразделов ответ не нужен, а ручка читает диск.
  const { data: nnMarks } = useQuery({
    queryKey: ['cabinet', 'nn-marks', species, date],
    queryFn: () => cabinetApi.nnMarks(species as number, date),
    enabled: model === 'hsi' && hsiSel === NN && species != null && !!date,
    staleTime: 5 * 60_000,
  })

  // Отметки на карту. Ключ слоя ОДИН (`nn`): двадцатка за дату ровно одна, и
  // накапливать их по датам нечего — при смене даты рисуется новая.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    // Пока идёт серия, поверх кадра ничего своего не рисуем: в архив уехал бы
    // кадр с отметками, которых человек не включал.
    if (seriesRef.current) return
    const draw = model === 'hsi' && hsiSel === NN && nnMarksOn && !!nnMarks?.present
    if (!draw) {
      map.removeNnMarks('nn')
      return
    }
    map.setNnMarks('nn', marksCollection(nnMarks!.marks))
    return () => {
      mapRef.current?.removeNnMarks('nn')
    }
  }, [model, hsiSel, nnMarks, nnMarksOn])

  // Что посчитано за дату — панель показывает пустые механизмы прочерком.
  const { data: upwPresent } = useQuery({
    queryKey: ['cabinet', 'upw-mech', date],
    queryFn: () => cabinetApi.upwellingMechanisms(date),
    enabled: model === 'upwelling' && !!date,
  })
  const upwPresentSet = useMemo(
    () => (upwPresent
      ? new Set(upwPresent.mechanisms.filter(m => m.data_present).map(m => m.mechanism))
      : null),
    [upwPresent],
  )
  // Горизонты, на которых поле механизма за эту дату ДЕЙСТВИТЕЛЬНО лежит.
  // Их не всегда четыре: дивергенция интегрируется к 30 и 50 м, и глубиномер
  // на 10 м отдал бы пустую карту при кнопке, которая её не исправит.
  const upwDepthsOf = useMemo(() => {
    const byMech = new Map(upwPresent?.mechanisms.map(m => [m.mechanism, m.depths]) ?? [])
    return (id: string) => byMech.get(id) ?? null
  }, [upwPresent])

  // Глубина — свойство МЕХАНИЗМА, а не имени слоя. Раньше здесь стояло
  // `model !== 'upwelling'`: раздел гасил глубиномер принудительно, потому что
  // старый индекс придонный. У семьи горизонты есть у эталона и дивергенции, и
  // погашенный глубиномер оставлял бы их линии неатрибутируемыми.

  // Ноль старой ссылки зажимается ОДИН раз, на входе в раздел. Непрерывный
  // зажим сделал бы 0 м недостижимым, и оговорка легенды («на 0 м вертикальный
  // ток почти нулевой») стала бы непроверяемой.
  const upwEnteredRef = useRef(false)
  useEffect(() => {
    if (model !== 'upwelling') {
      upwEnteredRef.current = false
      return
    }
    if (upwEnteredRef.current) return
    upwEnteredRef.current = true
    if (depth === 0) setDepth(snapDepthOnEnter(0))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model])


  const { data: speciesData } = useQuery({ queryKey: ['cabinet', 'species'], queryFn: () => cabinetApi.species() })
  const speciesList = useMemo(() => speciesData?.species ?? [], [speciesData])

  // Глубина гаснет и у поверхностного вида в «Комфортной среде»: его HSI один
  // на все горизонты (sectionRules.hsiDepthOff). Эффект стоит ПОСЛЕ каталога
  // видов — список зависимостей вычисляется на рендере.
  const surfaceHsi = hsiDepthOff(model, species, speciesData?.species)
  useEffect(() => {
    setDepthApplicable(
      model === 'upwelling' ? upwDepthApplicable(upwSel, upwCatalog) : !surfaceHsi,
    )
    setDepthNote(surfaceHsi ? 'context.depthSurface' : null)
  }, [model, upwSel, upwCatalog, surfaceHsi, setDepthApplicable, setDepthNote])
  const { data: clustersData } = useQuery({ queryKey: ['cabinet', 'clusters'], queryFn: () => cabinetApi.clusters() })

  // Накопительный снос: читаем готовые треки при выбранном виде. Окно наблюдений
  // одно — семь суток до выбранного дня (ползунок «Свежесть» убран 11.09.2026).
  // «Комфортной среде» снос нужен ради косяков (sectionRules.driftFetchedIn).
  const driftEnabled = history && driftFetchedIn(model, schools, catchShown) && species != null
  const { data: driftData, isFetching: driftLoading } = useQuery({
    enabled: driftEnabled,
    // Дата в ключе — окно свежести отсчитывается от просматриваемого дня, и при
    // перемотке НАБОР точек меняется, а не только их положение на треках.
    // Берём отстоявшуюся: резка по дню считается в браузере и остаётся
    // мгновенной, а в сеть ходим, когда человек остановился на дате.
    queryKey: ['cabinet', 'drift', species, settledDriftDate],
    queryFn: () => cabinetApi.drift(species!, settledDriftDate),
    // Пока едет ответ на новое окно, держим прежнюю картину: иначе слой сноса
    // мигал бы (снятие → добавление) на каждом шаге ползунка.
    placeholderData: prev => prev,
  })

  // Кнопка «Загрузить» = пересчёт: тайлы за дату перерисованы, а URL прежний и
  // отдаётся с max-age=86400 → без cache-buster'а браузер показал бы старую
  // картинку (сервер 304 не отдаёт, см. sharedTileUrl).
  //
  // layerRef — что за растр сейчас на карте и какая модель его показала. Нужен
  // двум сторонам: чтобы поздний ответ availability не воскресил снятый слой и
  // чтобы при уходе из раздела снять слой независимо от того, кто его показал —
  // фон или кнопка (иначе растр апвеллинга читался бы как слой HSI).

  // --- Серия кадров (архив PNG + параметры) -------------------------------
  //
  // Кадр показывается ПОДМЕНОЙ значений (`seriesOverride`), а не записью в общий
  // контекст: иначе адресная строка, «Поделиться» и перезагрузка посреди
  // прогона отдали бы чужой вид, а обрыв на третьем кадре оставил бы человека с
  // изотермой, которую он не ставил (см. map/seriesOverride.ts).
  const [seriesOverride, setSeriesOverride] = useState<ToolsView | null>(null)
  //: Идёт ли серия. Читают три эффекта растра: их поздние ответы иначе
  //: перебили бы кадр своим слоем (замер на MP4: тот же класс отказа).
  const seriesRef = useRef(false)
  //: Что было на карте до серии — туда и возвращаемся.
  const seriesRestoreRef = useRef<{ url: string; owner: ModelKind } | null>(null)

  const layerRef = useRef<{ url: string; owner: ModelKind } | null>(null)
  const showLayer = (url: string, owner: ModelKind, mode: 'replace' | 'refresh' = 'replace') => {
    // 'refresh' = setTiles на существующем слое: без пересоздания и без мигания
    // (важно на слайдере дней, где URL меняется каждый шаг).
    if (mode === 'refresh') mapRef.current?.refreshLayer(url)
    else mapRef.current?.showLayer(url)
    layerRef.current = { url, owner }
  }
  const clearLayer = () => {
    mapRef.current?.clearLayer()
    layerRef.current = null
  }

  // Пайплайн может досчитаться после того, как пользователь ушёл в другой раздел:
  // onReady держит `model` того рендера, где нажали кнопку, поэтому сверяемся с
  // актуальным — иначе растр HSI лёг бы поверх апвеллинга и остался бы там.
  const modelRef = useRef(model)
  modelRef.current = model
  // Актуальные дата/глубина для стража опроса тайлов: если пользователь сменит их
  // во время генерации, поздний опрос не должен применить URL за старые параметры.
  const dateRef = useRef(date)
  dateRef.current = date
  const depthRef = useRef(depth)
  depthRef.current = depth
  // Слой заливки раздела и ГОРИЗОНТ, на котором он лежит. Глубиномер кабинета
  // и горизонт механизма — разные числа: у первого семнадцать уровней, у
  // второго четыре (10/20/30/50), и ноль означает «горизонта у механизма нет».
  // Горизонт ПРОГОНА: считать надо и то, что ляжет линиями, а линии лежат на
  // горизонте даже при безглубинной заливке.
  const upwDepthEff = useMemo(
    () => (upwDepthApplicable(upwSel, upwCatalog) ? nearestMechDepth(depth) : 0),
    [upwSel, upwCatalog, depth],
  )
  // Горизонт ЗАЛИВКИ — свой: у безглубинного механизма его нет вовсе, и
  // общий горизонт приписал бы полю глубину, которой у него не бывает.
  const upwRasterDepth = useMemo(() => {
    const own = upwCatalog.find(m => m.id === upwSel.raster)
    if (!own?.has_depth) return 0
    return nearestMechDepth(depth, upwDepthsOf(upwSel.raster))
  }, [upwSel.raster, upwCatalog, depth, upwDepthsOf])
  // Подраздел раздела — ОДНОЙ переменной на обе семьи: `backgroundLayerFor` сам
  // разбирает, чей это ключ, и чужой в фон не пропустит. Две переменные на два
  // семейства разъехались бы в четырёх местах вызова.
  const sectionSub = model === 'hsi' ? hsiSel : upwSel.raster
  const upwLayerRef = useRef<string>(sectionSub)
  upwLayerRef.current = sectionSub
  // Вид нужен фоновому слою `nn`: он первый, считающийся ПО ВИДУ.
  const speciesRef = useRef<number | null>(species)
  speciesRef.current = species
  const upwDepthRef = useRef(upwRasterDepth)
  upwDepthRef.current = upwRasterDepth

  // Опрос готовности тайлов после «Загрузить»: воркер рисует их асинхронно, уже
  // после completed. Без опроса цветные тайлы появлялись лишь после жёсткой
  // перезагрузки. Триггерится нонсом из onReady (см. эффект ниже).
  const resolveOwnerRef = useRef<ModelKind | null>(null)
  const [resolveNonce, setResolveNonce] = useState(0)

  // Счётчик перерисовки слоя флота: фоновое обновление позиций из MarineTraffic
  // дописало точки в базу, а вьюпорт при этом не менялся — без своего ключа
  // слой не узнал бы, что данные обновились.
  const [fleetNonce, setFleetNonce] = useState(0)

  // Счётчик завершившихся докачек: инструменты наложения (изотермы, фронты,
  // волнение) держат поле, взятое один раз на вьюпорт, и без этого сигнала после
  // «Обновить» показывали бы до-докачечную сетку (см. overlayGridKey).
  const [loadNonce, setLoadNonce] = useState(0)

  const { state, progress, cardProgress, manifest, error, run, confirm, cancel } = useLoadData(url => {
    // Данные докачаны — сетку инструментов перечитать в любом случае, даже если
    // тайлы ниже относятся к уже смененной модели: качали их по вьюпорту, а не
    // по модели, и свежести это не отменяет.
    setLoadNonce(n => n + 1)
    if (modelRef.current !== model) return
    showLayer(bustedTileUrl(url, Date.now()), model)
    // Течения/апвеллинг: их готовность знает availability → запускаем опрос, чтобы
    // прозрачная/серая заглушка сама сменилась цветным тайлом, когда воркер дорисует.
    if (backgroundLayerFor(model, sectionSub)) {
      resolveOwnerRef.current = model
      setResolveNonce(n => n + 1)
    }
  },
  // Фоновое обновление позиций судов закончилось — перерисовать слой флота.
  () => setFleetNonce(n => n + 1),
  )

  const needsSpecies = model === 'hsi'
  // Нейрокарту кнопкой не посчитать: поле считает машина обучения и присылает
  // готовым артефактом. Кнопка, запускающая пайплайн ДРУГОГО подраздела,
  // утверждала бы, что она эту карту чинит, — а она её не трогает вовсе.
  const nnFill = model === 'hsi' && hsiSel === NN
  const canLoad = (!needsSpecies || species != null) && !nnFill

  const handleLoad = () => {
    const map = mapRef.current
    if (!map) return
    clearLayer()
    // Взводим цепочку «Траекторий по HSI»: если после докачки течений окажется,
    // что поле вида отсутствует или устарело, расчёт HSI запустится сам — но
    // только от этого клика (см. maybeRecomputeHsi).
    hsiArmedRef.current = hsiDriftEnabled
    // …и вместе с ней докачку недостающих суток истории течений: только этот
    // клик её и заказывает (фидбек владельца 2026-08-17 — полоска докачки шла
    // от простого перемещения карты).
    historyArmedRef.current = hsiDriftEnabled
    if (hsiDriftEnabled) setHsiProgress(null)
    // «Обновить» (кнопка была ready) → пропустить модалку, сразу докачка+пересчёт+тайлы.
    const refresh = state === 'ready'
    if (needsSpecies && species != null) {
      run({
        bbox: map.getBBox(), date, layers: ['probability'], species_id: species,
        depth, kind: 'model', tileUrl: probabilityTileUrl(species, date, depth),
      }, { refresh })
    } else if (model === 'upwelling') {
      // Безвидовая модель. Считаем ТОЛЬКО выбранное: listKey слоёв задаёт объём
      // прогона (`upwelling_scope` на сервере), и лишний `upwelling` в нём
      // стоил бы сорока минут прохода 1/240° на каждый клик.
      const wanted = [upwSel.raster, ...upwSel.iso]
      run({ bbox: map.getBBox(), date, layers: withSectionLayers(wanted),
        depth: upwDepthEff,
        kind: 'upwelling', tileUrl: upwTileUrl() }, { refresh })
    } else if (model === 'currents') {
      run({ bbox: map.getBBox(), date,
        layers: withSectionLayers(['currents']),
        depth, kind: 'raw',
        tileUrl: sharedTileUrl(date, 'currents', depth, null, palette) }, { refresh })
    }
  }

  // Адрес тайла раздела. Собирает его ОДИН `backgroundTileUrl` — тот же, что
  // красит фон и применяет опрос после «Загрузить»: вторая точка сборки
  // разъехалась бы с первой при первой же правке (так расходились палитры
  // карты и пороги скорости судна).
  const upwTileUrl = (version?: string | null) =>
    backgroundTileUrl(
      backgroundLayerFor('upwelling', upwSel.raster) as BackgroundLayer,
      date, upwRasterDepth, version ?? null, palette,
    ) as string   // семья апвеллинга безвидовая — адрес собирается всегда

  // Фон раздела (см. backgroundLayerFor): течения и апвеллинг показываем сразу,
  // без кнопки «Загрузить» — они уже посчитаны, кнопка означала бы пересчёт.
  //
  // Как в «Картах»: оптимистично показываем имеющиеся тайлы (проверка охвата
  // тяжёлая, ждать её нельзя), затем availability уточняет. Отличие от «Карт» —
  // здесь нет прогрессивного докрашивания: если готовых тайлов нет, снимаем
  // пустой слой и оставляем работу кнопке «Загрузить» с её обычным пайплайном.
  //
  // Дату для фона берём ДЕБАУНСНУТУЮ: слайдер дней меняет `date` на каждый шаг,
  // и без задержки перетаскивание превратилось бы в шторм запросов availability.
  // Точки при этом двигаются мгновенно — они считаются локально.
  const [bgDate, setBgDate] = useState(date)
  useEffect(() => {
    const id = setTimeout(() => setBgDate(date), 250)
    return () => clearTimeout(id)
  }, [date])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    // Пока идёт серия, растром распоряжается она: этот эффект показал бы слой
    // раздела поверх снимаемого кадра — и в архив уехала бы чужая карта.
    if (seriesRef.current) return
    // Растр, показанный ДРУГИМ разделом, под этой моделью читался бы как её слой.
    if (layerRef.current && layerRef.current.owner !== model) clearLayer()

    const layer = backgroundLayerFor(model, sectionSub)
    if (!layer) return
    // Горизонт: у течений с глубиномера, у семьи апвеллинга свой (см.
    // upwDepthEff), у нейрокарты его нет вовсе — сеть отвечает о столбе воды
    // целиком, и горизонт витрины её ответа не меняет.
    const bgDepth = layer === 'currents' ? depth : layer === 'nn' ? 0 : upwRasterDepth

    let alive = true
    const url = backgroundTileUrl(layer, bgDate, bgDepth, null, palette, species)
    // Адрес не собрался — у видового слоя не спросили вид. Слой не показываем и
    // не снимаем чужой: `clearLayer` выше уже отработал.
    if (!url) return
    showLayer(url, model, 'refresh')
    cabinetApi
      .availability({ layer, date: bgDate, depth: bgDepth, bbox: map.getBBox(),
                      species_id: species })
      .then(av => {
        if (!alive || seriesRef.current) return
        // Оптимистичный показ шёл по URL без версии → гарантированно из кэша
        // браузера. Узнав версию, переприменяем слой, иначе на экране осталась бы
        // старая картинка.
        const versioned = backgroundTileUrl(
          layer, bgDate, bgDepth, av.tiles_version ?? null, palette, species)
        if (!versioned) return
        const decision = backgroundDecision({
          av, shownUrl: url, onMapUrl: layerRef.current?.url ?? null, versionedUrl: versioned,
        })
        if (decision === 'refresh') {
          map.refreshLayer(versioned)
          layerRef.current = { url: versioned, owner: model }
        } else if (decision === 'clear') {
          clearLayer()
        }
      })
      .catch(() => {}) // сеть/ошибка — оставляем оптимистичный слой, кнопка доступна
    return () => {
      alive = false
    }
    // Вид входит в зависимости: у нейрокарты он в АДРЕСЕ тайла, и без него смена
    // вида читалась бы из замыкания прошлого рендера — карта осталась бы чужой.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, bgDate, depth, sectionSub, upwRasterDepth, species])

  // Авторазрешение тайлов после «Загрузить» (нонс из onReady). Тайлы shared/
  // upwelling рисуются воркером АСИНХРОННО — задача completed раньше, чем PNG
  // на диске. Опрашиваем availability и, дождавшись tiles_ready, применяем
  // версионированный URL — цветной тайл появляется сам, без перезагрузки.
  useEffect(() => {
    if (resolveNonce === 0) return
    const owner = resolveOwnerRef.current
    if (!owner) return
    const bg = backgroundLayerFor(owner, upwLayerRef.current)
    if (!bg) return
    const map = mapRef.current
    if (!map) return

    let alive = true
    const pollDate = dateRef.current
    const pollDepth = bg === 'currents' ? depthRef.current
      : bg === 'nn' ? 0 : upwDepthRef.current
    const pollLayer = upwLayerRef.current
    const pollSpecies = speciesRef.current
    // Страж: если пользователь сменил модель/дату/глубину, поздний ответ не должен
    // воскресить слой за устаревшие параметры (как backgroundDecision у фона).
    const stillOurs = () =>
      alive &&
      modelRef.current === owner &&
      dateRef.current === pollDate &&
      upwLayerRef.current === pollLayer &&
      (bg === 'currents' ? depthRef.current
        : bg === 'nn' ? 0 : upwDepthRef.current) === pollDepth &&
      speciesRef.current === pollSpecies

    void pollTilesReady({
      layer: bg,
      date: pollDate,
      depth: pollDepth,
      availability: p => cabinetApi.availability(p),
      bbox: () => map.getBBox(),
      species: pollSpecies,
      applyVersioned: url => {
        if (!stillOurs()) return
        map.refreshLayer(url)
        layerRef.current = { url, owner }
      },
      sleep: ms => new Promise(r => setTimeout(r, ms)),
      alive: stillOurs,
    })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveNonce])

  // Смена ВИДА / ГЛУБИНЫ / ДАТЫ перерисовывает слой probability-моделей (HSI /
  // сезонный прогноз): URL тайла — probability_{depth} по конкретному виду и дате,
  // тайлы рендерятся лениво из pf (все глубины уже посчитаны). У этих моделей нет
  // фонового эффекта (в отличие от течений/апвеллинга), поэтому раньше смена вида
  // (как и глубины) карту не обновляла. Переставляем URL слоя — но ТОЛЬКО если
  // слой этой модели уже показан кнопкой (не автопоказ без «Загрузить»). Если вид
  // сброшен в «Все виды» — probability без вида не существует, снимаем слой.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (seriesRef.current) return
    if (!shouldRepointProbabilityLayer(model, layerRef.current?.owner ?? null)) return
    if (species == null) {
      clearLayer()
      return
    }
    showLayer(probabilityTileUrl(species, date, settledDepth), model, 'refresh')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [species, date, settledDepth])

  // АВТОПОКАЗ готового поля HSI при входе в модель.
  //
  // Живая жалоба: «переключаюсь на HSI — карта серая», при том что на сервере
  // всё нарисовано, а траектории по HSI поверх этой серости рисуются (они берут
  // поле прогноза напрямую, минуя тайлы). Слой показывался ИСКЛЮЧИТЕЛЬНО кнопкой
  // внутри HSI-вида: клик, сделанный в течениях, ему не засчитывался — и человек
  // видел доказательство существования поля рядом с пустой картой.
  //
  // Спрашиваем не availability (у модельных слоёв он отвечает «не готово» по
  // определению), а статус растра: есть ли чем красить ИМЕННО ЭТУ глубину.
  useEffect(() => {
    if (model !== 'hsi' || species == null) return
    if (seriesRef.current) return
    if (layerRef.current?.owner === 'hsi') return      // уже показан — не мешаем
    let alive = true
    const shownDate = date
    const shownDepth = settledDepth
    void cabinetApi
      .rasterStatus(species, date)
      .then(status => {
        // Страж поздних ответов: пользователь мог уйти на другую модель/дату.
        if (!alive || modelRef.current !== 'hsi' || seriesRef.current) return
        if (dateRef.current !== shownDate || depthRef.current !== shownDepth) return
        if (!probabilityAutoShow(status, shownDepth)) return
        // С версией: тайлы кэшируются на сутки при неизменном URL, и без
        // неё перерисованное поле не доезжает до экрана даже после
        // перезагрузки страницы.
        showLayer(
          probabilityTileUrl(species, shownDate, shownDepth, status?.tiles_version),
          'hsi',
        )
      })
      .catch(() => {})    // сеть моргнула — просто остаёмся при кнопке
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, species, date, settledDepth])

  // Стрелки направления течений поверх фона. Слой течений рисует только МОДУЛЬ
  // скорости, поэтому без стрелок не видно, куда идёт вода.
  //
  // Плотность считается от зума (arrows.ts): шаг подбирается так, чтобы
  // расстояние между стрелками на экране было постоянным, а длина ограничена
  // сверху — соседние стрелки не соприкасаются ни на одном масштабе.
  const centerKey = mapView.center ? mapView.center.join(',') : ''
  const zoom = mapView.zoom ?? 4
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (model !== 'currents') {
      map.removeArrows('currents')
      return
    }
    let alive = true
    const id = setTimeout(() => {
      cabinetApi
        .currentsArrows({
          date: bgDate,
          depth,
          stepDeg: arrowStepDeg(zoom),
          bbox: map.getBBox(),
          waves: waveOverlay,
        })
        .then(fc => {
          if (!alive) return
          // Пусто (нет течений на дату / всё слабее порога) — снимаем слой,
          // а не оставляем прошлую картинку поверх другой даты.
          if (!fc.features.length) {
            map.removeArrows('currents')
            setArrowsState({ kind: 'empty' })
            return
          }
          map.setArrows('currents', fc as GeoJSON.FeatureCollection, arrowScale(zoom), waveOverlay)
          setArrowsState({ kind: 'ok', truncated: fc.meta?.truncated })
        })
        .catch(err => {
          if (!alive) return
          // Молчать нельзя: поле ПРЕДЫДУЩЕЙ даты под сегодняшней подписью — ложь,
          // а пустая карта без объяснения читается как баг отрисовки.
          map.removeArrows('currents')
          setArrowsState({ kind: 'error', status: err?.status })
        })
    }, 250)
    return () => {
      alive = false
      clearTimeout(id)
    }
    // `state` в зависимостях — чтобы после «Загрузить/Обновить» стрелки
    // перезапрашивались. Без этого сбой на момент докачки был необратим:
    // дата и зум не менялись, эффект не перезапускался, и стрелки не
    // возвращались, пока не подвинешь карту. waveOverlay — тумблер «Волнение»
    // перекрашивает стрелки (нужен запрос wave_hs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, bgDate, depth, zoom, centerKey, state, waveOverlay])

  // Единый инструмент температуры (изотерма + градиенты) — общий хук; состояние в
  // ViewContext (блок «Наложения»), фикс 5–16 °C, над любым слоем (не только течения).
  const {
    sstData, sstPartial, isothermDrawn, frontsDrawn, surfaceData, surfaceDrawn,
    pending: tempPending,
  } = useTemperatureTools(mapRef, `${centerKey}|${zoom}`, loadNonce, seriesOverride)

  // Изолинии остальных механизмов поверх заливки.
  const { pending: upwIsoPending } = useUpwellingIso(mapRef, {
    enabled: model === 'upwelling',
    mechanisms: upwSel.iso,
    date,
    depth: settledDepth,
    viewKey: `${centerKey}|${zoom}`,
    loadNonce,
    zero: upwSel.zero,
    depthsOf: upwDepthsOf,
  })

  // Оверлей «Волнение»: изо-линии высоты волны (над любым слоем) + возвращает источник.
  const { source: waveSource, hasData: waveData, drawn: waveDrawn, pending: wavePending } =
    useWaveIso(mapRef, `${centerKey}|${zoom}`, loadNonce, seriesOverride)

  // --- «Траектории по HSI» («живая вода») ---------------------------------------
  // Отдельный слой поверх течений: обратный лагранжев снос ненулевых HSI-ячеек
  // вида. Требует конкретного вида (не «Все виды»). Снос косяков (drift/clusters)
  // — независимый слой, здесь не участвует.
  // Под серией условие берётся у КАДРА: усики wanted на первом кадре независимо
  // от того, в каком режиме человек стоял, когда нажал кнопку.
  // Без истории усиков нет вовсе: обратная адвекция идёт по прошлой неделе.
  const hsiDriftEnabled = history && (seriesOverride
    ? seriesOverride.hsiDrift && species != null
    : model === 'currents' && species != null && hsiDrift)

  // ЕДИНСТВЕННЫЙ признак «кометы реально идут». Держим его один на всех, потому
  // что расхождение условий уже стоило нам пустой карты: эффект нарезки прятал
  // статичные усики по «нажата ли кнопка», а эффект такта отказывался
  // анимировать по prefers-reduced-motion — и пользователь оставался без обоих
  // слоёв при «нажатой» кнопке. Пока флаг один и тот же в гварде нарезки, в
  // такте и в пропе кнопки, такое состояние невыразимо: либо кометы, либо
  // статика, а надпись на кнопке всегда описывает то, что на карте.
  // Запись входит в решение сама (см. hsiCometMode): на время ролика кометы
  // идут и статика снята даже при системном «меньше движения» — иначе кадр
  // записи рисовал кометы поверх усиков, оставленных на карте.
  const comets = cometMode({
    enabled: hsiDriftEnabled,
    trails: hsiDriftData?.features.length ?? 0,
    reduced,
    animate: hsiAnim,
    recording,
  })
  const cometsRunning = comets.running

  // Тумблер слоя выключили — гасим и режим анимации. Иначе `hsiAnim` переживает
  // выключение, и следующее включение слоя стартует с анимации, которую никто
  // не просил.
  useEffect(() => {
    if (!hsiDriftEnabled) setHsiAnim(false)
  }, [hsiDriftEnabled])

  // ВКЛЮЧЕНИЕ тумблера — такое же явное действие, как клик по «Обновить»: взводим
  // цепочку пересчёта. Иначе получалось так (Чукотка, сельдь, 11.08.2026): человек
  // качает данные, докачка помечает поле вида устаревшим, потом человек включает
  // «Траектории HSI» — и остаётся с пустой картой навсегда, потому что взвести
  // цепочку умел только клик, а его уже сделали. Взводим на ПЕРЕХОД выкл→вкл, а
  // не на каждый рендер: сама попытка одноразовая (hsiArmedRef снимается первой
  // же проверкой), и панорамирование её не воскрешает.
  const hsiDriftWasOn = useRef(hsiDriftEnabled)
  useEffect(() => {
    if (hsiDriftEnabled && !hsiDriftWasOn.current) {
      hsiArmedRef.current = true
      setHsiProgress(null)
    }
    hsiDriftWasOn.current = hsiDriftEnabled
  }, [hsiDriftEnabled])

  // Вторая ступень цепочки «Обновить»: течения уже докачаны — если причина в
  // самом HSI (нет поля, или докачка разъела его устаревшими ячейками), считаем
  // поле и перезапрашиваем усики. Прочие причины (вид тут не живёт, нет течений)
  // пересчётом не чинятся, и гонять на них минуты работы нечестно.
  //
  // Условие «усиков нет» тут НЕ действует: разъеденный кадр отдаёт часть усиков
  // и всё равно требует пересчёта (регрессия 2026-08-06 — обрыв посреди экрана).
  const maybeRecomputeHsi = (fc: HsiDriftCollection) => {
    const map = mapRef.current
    const armed = hsiArmedRef.current
    if (!map || species == null || hsiBusyRef.current) return
    const args = {
      armed,
      enabled: hsiDriftEnabled,
      // Усики считаются ПО течениям: пока пайплайн качает, расчёт HSI считал бы
      // по старым. Замок при этом НЕ снимаем — ждём. 'idle' тоже «можно»:
      // пересчёт заказан тумблером, докачки не было и ждать нечего.
      ready: currentsSettled(state),
      reason: fc.meta.reason ?? null,
      anchor: fc.meta.anchor ?? null,
      date: dateRef.current,
    }
    if (!shouldRecomputeHsi(args)) {
      // Течения ещё качаются — это не отказ, а «рано»: замок держим до 'ready',
      // иначе цепочка потратит свою единственную попытку на промежуточный ответ
      // (эффект усиков срабатывает на каждую смену состояния пайплайна).
      if (!args.ready) return
      hsiArmedRef.current = false
      return
    }
    hsiArmedRef.current = false          // одна попытка на клик
    hsiBusyRef.current = true
    void runHsiRecompute(
      {
        species,
        date: dateRef.current,
        anchor: args.anchor,
        depth: depthRef.current,
        bbox: map.getBBox(),
      },
      {
        prepare: cabinetApi.prepare,
        confirm: cabinetApi.confirm,
        progress: cabinetApi.progress,
        onProgress: p => {
          setHsiProgress(p)
          if (!p.done) return
          hsiBusyRef.current = false
          // Успех → перезапрос усиков. Серверный кэш ключуется calculated_at,
          // поэтому новый расчёт промахивается по нему сам.
          if (!p.error) setHsiNonce(n => n + 1)
        },
      },
    )
  }

  // Выборку усиков грузим ОДИН раз на (вид, глубина, вьюпорт) — как сетку SST.
  // Дату шлём стабильную (сегодня): бэкенд сам берёт последний прогноз ≤ неё как
  // якорь, поэтому ползунок дней (date) в deps НЕ входит — он лишь режет длину
  // усика по кэшу, без сети. `state` — чтобы после «Загрузить/Обновить» обновиться.
  /** Довести докачку истории течений до конца: опрашиваем задания, держим
   *  полоску, по завершении перезапрашиваем слой — усики станут длиннее. */
  const watchHistoryJobs = useCallback(
    async (jobs?: Array<{ task_id: string }> | null) => {
      if (!jobs?.length || historyBusyRef.current) return
      historyBusyRef.current = true
      try {
        for (let i = 0; i < HISTORY_POLL_ATTEMPTS; i++) {
          const statuses = await Promise.all(
            jobs.map(j =>
              cabinetApi
                .progress(j.task_id)
                // Не ответивший опрос — это НЕ провал докачки: задача могла
                // завершиться успешно. Разводим их, иначе полоска объявляет
                // пробел в данных источника по своей же сетевой ошибке.
                .catch(() => ({ progress_percent: 0, status: STATUS_UNKNOWN })),
            ),
          )
          const p = historyProgress(statuses)
          setHistoryProg(p)
          if (p?.done) break
          await new Promise(r => setTimeout(r, HISTORY_POLL_MS))
        }
      } finally {
        historyBusyRef.current = false
        // Данные приехали — просим слой заново: усики отрастут на закачанной
        // истории. Полоску оставляем на экране (она уже говорит «готово»).
        setHsiNonce(n => n + 1)
      }
    },
    [],
  )

  useEffect(() => {
    const map = mapRef.current
    if (!map || !hsiDriftEnabled || species == null) {
      setHsiDriftData(null)
      // Слой выключили/ушли со слоя течений — полоске расчёта показывать нечего.
      // Идущий расчёт при этом не рвём: он досчитается и пригодится следующему
      // включению (задание живёт на сервере, а не во вкладке).
      if (!hsiBusyRef.current) setHsiProgress(null)
      return
    }
    let alive = true
    const id = setTimeout(() => {
      // Докачку истории заказывает только клик по «Загрузить/Обновить», и ровно
      // один раз на него: этот же запрос уходит на каждый сдвиг карты и зум.
      // Пока пайплайн кнопки качает дату, замок держим — дыры в истории надо
      // считать по тому, что уже доехало (та же логика, что у пересчёта HSI).
      const fillHistory = shouldFillHistory({
        armed: historyArmedRef.current,
        ready: currentsSettled(state),
      })
      if (fillHistory) historyArmedRef.current = false
      cabinetApi
        .hsiDrift({
          // Конец усика — ВЫБРАННЫЙ день, а не сегодня: длина всегда 7 суток,
          // какая бы дата ни стояла на ползунке (замечание 11.09.2026).
          species, date: settledDriftDate, depth, strideDeg: arrowStepDeg(zoom),
          bbox: map.getBBox(), fillHistory,
        })
        .then(fc => {
          if (!alive) return
          setHsiDriftData(fc)
          // Решение «нужен ли расчёт HSI» принимается по СВЕЖЕМУ ответу — уже
          // после докачки течений, а не по причине, лежавшей на экране до клика.
          maybeRecomputeHsi(fc)
          void watchHistoryJobs(fc.meta?.history_jobs)
        })
        .catch(() => alive && setHsiDriftData(null))
    }, 250)
    return () => {
      alive = false
      clearTimeout(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hsiDriftEnabled, species, depth, centerKey, zoom, state, hsiNonce, settledDriftDate])

  // Усики на карту целиком: origin (7 сут до выбранного дня, кружок) → «сейчас»
  // (сам выбранный день). Резать их по ползунку больше нечего — дату получает
  // запрос. Пусто/нет данных/вид не выбран → снять слой (не оставлять картину
  // прошлой выборки).
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const meta = hsiDriftData?.meta
    // Анимация показывает те же усики кометами — статичные линии на это время
    // скрываются целиком (решение владельца), иначе кадр перегружен. Прячем
    // по `cometsRunning`, а не по «нажата кнопка»: если кометы не идут (reduced
    // motion, данные ушли), статика обязана остаться на карте.
    if (!hsiDriftEnabled || cometsRunning || !hsiDriftData || !meta?.origin ||
        hsiDriftData.features.length === 0) {
      map.removeHsiDrift('currents')
      return
    }
    const { lines, origins } = hsiDriftShapes(hsiDriftData)
    map.setHsiDrift('currents', lines, origins)
  }, [hsiDriftData, hsiDriftEnabled, cometsRunning])

  // Такт анимации: один requestAnimationFrame на всю карту. Сеть не трогаем —
  // геометрия усиков уже в памяти, меняется только положение комет.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    // Тот же флаг, что прячет статику, — иначе два эффекта расходятся во мнении
    // о происходящем и карта остаётся пустой (см. комментарий у cometsRunning).
    if (!cometsRunning || !hsiDriftData) {
      map.removeHsiComets('currents')
      return
    }

    cometTrailsRef.current = prepareTrails(hsiDriftData)
    let raf = 0
    let lastDraw = 0

    const tick = (now: number) => {
      raf = requestAnimationFrame(tick)
      // Кадр собирается на CPU, поэтому держим ~30 fps: глазу этого хватает,
      // а бюджет кадра остаётся карте.
      if (now - lastDraw < 32) return
      lastDraw = now
      if (document.hidden) return
      // Идёт запись — фазу задаёт она, свободный такт молчит. Иначе два
      // источника кадра дерутся, и в ролик попадает чужая фаза.
      if (recordingRef.current) return
      // Часы — от общего нуля документа, без запомненного `start`: фазы комет
      // привязаны к своему усику, абсолютное начало отсчёта не значит ничего.
      // Локальный `start` при этом ронял анимацию в начало на каждом перезапуске
      // эффекта (пан, зум, смена глубины) — кометы прыгали.
      const clock = (now % COMET_CYCLE_MS) / COMET_CYCLE_MS
      map.setHsiComets('currents', buildCometFrame(cometTrailsRef.current, clock))
    }
    raf = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(raf)
      map.removeHsiComets('currents')
    }
  }, [cometsRunning, hsiDriftData])

  // Ушли со слоя течений → снять усики HSI (currents-only). Температурные линии
  // (инструмент «Наложения») теперь общие над любым слоем — их не снимаем.
  useEffect(() => {
    if (model === 'currents') return
    mapRef.current?.removeHsiDrift('currents')
  }, [model])

  // Придонные стрелки апвеллинга. Источник — артефакт расчёта (то же поле, что
  // под ними), а НЕ таблица течений: отрыв планктона создаёт придонное течение
  // на склоне, и поверхностные стрелки поверх этой картинки показывали бы
  // направление, которого в расчёте нет. Поэтому слой отдельный, со своим
  // ключом: раздел «Течения» продолжает жить своими стрелками.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (model !== 'upwelling') {
      map.removeArrows('upwelling')
      return
    }
    let alive = true
    const id = setTimeout(() => {
      cabinetApi
        .upwellingArrows({ date: bgDate, stepDeg: arrowStepDeg(zoom), bbox: map.getBBox() })
        .then(fc => {
          if (!alive) return
          // Пусто — снимаем слой, а не оставляем стрелки прошлой даты поверх
          // сегодняшнего поля (та же ложь, что и у течений).
          if (!fc.features.length) {
            map.removeArrows('upwelling')
            setArrowsState({ kind: 'empty' })
            return
          }
          map.setArrows('upwelling', fc as GeoJSON.FeatureCollection, arrowScale(zoom))
          setArrowsState({ kind: 'ok' })
        })
        .catch(err => {
          if (!alive) return
          // Молчать нельзя (то же правило, что у течений): исчезнувшие стрелки
          // читаются как «придонных течений тут нет», хотя это сбой запроса.
          map.removeArrows('upwelling')
          setArrowsState({ kind: 'error', status: err?.status })
        })
    }, 250)
    return () => {
      alive = false
      clearTimeout(id)
    }
    // `state` — как у течений: после «Загрузить» артефакт появляется, и слой
    // обязан перезапроситься без движения карты.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, bgDate, zoom, centerKey, state])

  // Изобаты в разделе «Апвеллинг»: поле индекса без них читается как абстрактное
  // пятно — весь смысл в том, что нити идут вдоль свалов глубин (как на
  // телеграм-карте). Даты у слоя нет — рельеф дна статичен, поэтому запрос
  // зависит только от вьюпорта и (округлённого) зума: он задаёт упрощение линий
  // на бэкенде, а без округления инерция зума гнала бы шторм ответов.
  const isobathZoom = isobathZoomStep(zoom)
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (model !== 'upwelling') {
      map.removeIsobaths('upwelling')
      return
    }
    let alive = true
    const id = setTimeout(() => {
      cabinetApi
        .isobaths({ bbox: map.getBBox(), zoom: isobathZoom })
        .then(fc => {
          if (!alive) return
          if (!fc.features.length) {
            map.removeIsobaths('upwelling')
            setIsobathsFailed(false)   // вне покрытия таблицы — это не сбой
            return
          }
          map.setIsobaths('upwelling', fc as GeoJSON.FeatureCollection)
          setIsobathsFailed(false)
        })
        .catch(() => {
          if (!alive) return
          // Пропавшие изобаты неотличимы от бага отрисовки — говорим прямо.
          map.removeIsobaths('upwelling')
          setIsobathsFailed(true)
        })
    }, 250)
    return () => {
      alive = false
      clearTimeout(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Сырой `zoom`, а не округлённый: зум вокруг центра не меняет ни центр, ни
    // округлённый шаг, и линии остались бы обрезанными по прежнему вьюпорту.
    // Шторма нет — зум приходит только по moveend, плюс дебаунс 250 мс.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, isobathZoom, zoom, centerKey])

  // Флот (живые AIS-суда) и оверлеи — общие хуки: рисуются на карте и «Аналитики»,
  // и «Карт» из одного стора (ViewContext, блок «Наложения»).
  useVesselFocus(mapRef, focusVessel, setVesselPick)
  // Шаг изобат под серией — из кадра: линии кадра «Дрейфы» идут через 10 м
  // независимо от того, что стоит у человека в сайдбаре.
  const shownIsobathStep = seriesOverride ? seriesOverride.isobathStep : isobathStep
  const { drawn: isobathsDrawn, pending: isobathPending } = useIsobathTool(
    mapRef, shownIsobathStep != null, shownIsobathStep ?? 0, `${centerKey}|${zoom}`,
  )
  useFleetLayer(mapRef, {
    fleet,
    species,
    showUncategorized,
    flags,
    onFlagCounts: setFlagCounts,
    viewKey: `${centerKey}|${zoom}`,
    orgId: activeOrgId,
    refreshKey: fleetNonce,
    // Закреплённая карточка судна держит снимок свойств, снятый в миг клика, и
    // сама о переезде судна не узнаёт — из-за этого кнопка «Обновить данные»
    // 06.09.2026 выглядела сломанной при отработавшем задании (см.
    // `models/cardPick.refreshedPick`).
    onVessels: features => setVesselPick(p => refreshedPick(p, features)),
    // Идёт запись — позиции судов ставит кадр. По окончании флаг гаснет, эффект
    // перезапускается и сам возвращает на карту живые позиции.
    paused: recording,
  })
  // Оверлеи, которые сейчас НА КАРТЕ. Под серией это оверлеи кадра, а не выбор
  // человека: кадр замещает вид целиком. В адресную строку они не уезжают —
  // там по-прежнему пользовательские, иначе «Поделиться» посреди прогона отдал
  // бы чужой вид, а перезагрузка открыла бы кадр серии вместо рабочей карты.
  const shownOverlays = seriesOverride?.overlays ?? overlays
  const { earthquakeCount } = useOverlays(mapRef, shownOverlays, bgDate)
  // Рубежи ИЭЗ — оверлей с ВЬЮПОРТОМ (в отличие от землетрясений): линии
  // режутся и упрощаются по кадру, поэтому свой хук, а не useOverlays.
  const eezMeta = useEezLayer(mapRef, shownOverlays.includes('eez'), `${centerKey}|${zoom}`)
  const districtsMeta = useDistrictsLayer(mapRef, shownOverlays.includes('fishing_districts'), `${centerKey}|${zoom}`)
  const substrateMeta = useSubstrateLayer(mapRef, shownOverlays.includes('substrate'), `${centerKey}|${zoom}`)
  // Полигон карты дна под точкой отсчёта: берётся у НАРИСОВАННОГО слоя, отдельного
  // запроса к серверу не нужно — свойства лежат в самой фиче.
  const pointSubstrate = useMemo(() => {
    const map = mapRef.current?.getMap()
    const pick = pointCard.pick
    return map && pick ? substrateAt(map, pick.lng, pick.lat) : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointCard.pick, substrateMeta.classes])
  // Запреты — тот же тумблер, но с датой и видом раздела.
  const bansMeta = useBansLayer(mapRef, shownOverlays.includes('fishing_districts'),
    `${centerKey}|${zoom}`, date, species)
  // Навигационные предупреждения NGA — в силе на дату раздела.
  const navwarnMeta = useNavwarnLayer(mapRef, shownOverlays.includes('navwarn'),
    `${centerKey}|${zoom}`, date)
  // Что нарисовали промрайоны и запреты и чья оговорка: в Омане районов нет,
  // а у запретов своя подпись — экран и кадр получают это одним объектом.
  const linesDrawn = {
    districts: districtsMeta.disclaimerKey != null,
    bans: bansMeta.drawn ?? undefined,
    bansDisclaimerKey: bansMeta.disclaimerKey,
    bansEverywhere: bansMeta.everywhere,
    substrate: substrateMeta.classes,
    navwarn: navwarnMeta.kinds,
    // Под серией места не рисуются (см. эффект отметок) — и в легенде их нет.
    nnMarks: model === 'hsi' && hsiSel === NN && nnMarksOn && nnMarks?.present && !seriesOverride
      ? nnMarks.marks.length : 0,
  }
  // Карточка землетрясения — общая связка на оба раздела (см. useQuakeCard).
  const quakeCard = useQuakeCard()
  // Карточка рубежа ИЭЗ: чей он и на каком основании. Без неё линию на карте
  // нечем назвать — вопрос «откуда она взялась» решался запросом к базе.
  const eezCard = useEezCard()

  // косяки как маркеры (в режиме «по течениям»). Каждая точка привязана к виду:
  // при выбранном виде показываем только его косяки (как и дрейф), без вида — все.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    // Тумблер «Косяки» гасит и этот слой, и снос тех же косяков (driftForDay):
    // косяк рисуется двумя слоями, и половинчатое выключение оставило бы на
    // карте ровно те отметки, ради которых тумблер нажимают.
    if (!schoolsShownIn(model) || !schools) {
      map.removeGeoJson('clusters')
      return
    }
    const fc: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      // Отметку косяка, у которого есть трек, рисует слой СНОСА (role='origin') —
      // здесь оставляем только тех, кого снос ещё не считал, иначе в одной точке
      // окажется два кружка.
      features: clustersWithoutTrack(
        visibleClusters(clustersData?.clusters ?? [], species, date),
        driftEnabled ? (driftData ?? null) : null,
      ).map(c => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [c.lon, c.lat] },
        // radius в свойстве → размер кружка по размеру косяка (req 2)
        properties: { id: c.id, note: c.note, radius: clusterRadius(c.size_class) },
      })),
    }
    map.setGeoJson('clusters', fc, CLUSTER_COLOR, ['get', 'radius'], clusterOpacity(driftEnabled))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, clustersData, species, date, driftEnabled, driftData, schools])

  // Картина сноса на ВЫБРАННЫЙ день: срез уже загруженных треков, без запроса
  // (см. docs/plans/2026-07-20-drift-day-slider-design.md).
  // `today` — якорь: при перемотке рисуются два маркера (сегодняшний приглушённый
  // и дневной яркий) и трек между ними. На нуле картина ровно как была.
  // Косяки отсеиваются ДО среза, а не после: срез строит куски трека и якоря
  // «сегодня», и выбрасывать их потом значило бы считать то, чего не покажут.
  const driftForDay = useMemo(
    () => {
      const src = driftForSection(applySchoolsVisibility(driftData ?? null, schools), model, catchShown)
      return src ? sliceDriftCollection(src, date, today()) : null
    },
    [driftData, date, schools, model, catchShown],
  )

  // Нарисован ли снос в кадре. Один признак на три места (слой, карточка-легенда
  // на экране, легенда на скачанной картинке) — иначе файл объяснял бы то, чего
  // на нём нет, или молчал о том, что есть.
  const driftVisible = driftEnabled && !!driftForDay && driftForDay.features.length > 0

  // Кабинету закрыты точные места вылова: маркеров вылова в кадре нет, есть
  // только ореолы районов. Признак приходит от бэкенда (он же и огрубляет
  // координаты) и живёт в двух легендах — экранной и той, что уходит в файл.
  // Только в «Дрейфах»: «Комфортная среда» вылова не рисует, и подпись про его
  // огрубление объясняла бы то, чего на карте нет.
  const catchAnonymized =
    driftVisible && (model === 'currents' || catchShown) && driftData?.meta?.catch_anonymized === true

  // Видимость фактического вылова — настройка кабинета (заказ владельца
  // 2026-08-13). Точки и ореол гасятся ПОРОЗНЬ, гасит их бэкенд (координаты
  // огрубляет, радиусов не отдаёт), сюда приходит только объяснение для легенды
  // — экранной и той, что уходит в файл. Скрытые точки живут в `catchAnonymized`:
  // это одно состояние, а не два.
  const catchHaloHidden =
    driftVisible && (model === 'currents' || catchShown) && driftData?.meta?.catch_halo_hidden === true

  // Оверлей сноса: линии-треки + снесённые точки. Слой 'drift' сосуществует с 'clusters'
  // (разные source/слои в MapCanvas) — отметка косяков не затрагивается.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    // Снимаем слой при выключении/смене режима/пустой коллекции — «честное падение».
    // Пустой срез — нормальная ситуация: на выбранный день отметок ещё не было.
    if (!driftVisible || !driftForDay) {
      map.removeDrift('drift')
      return
    }
    map.setDrift('drift', driftForDay)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driftVisible, driftForDay])

  // Карточки объектов (судно, снесённая точка, косяк) независимы: закреплённые
  // висят одновременно и закрываются каждая своим крестиком. Взаимно гасим только
  // ФОРМЫ (новая отметка ↔ правка существующей) — это два состояния одного дела.
  const onMapClick = (p: { lng: number; lat: number; x: number; y: number }) => {
    // В режиме течений клик — это РАЗМЕТКА: им ставят отметку косяка, и жест
    // этот в ходу давно. Отсчёт по той же кнопке отобрал бы его, поэтому здесь
    // карточка уступает форме (решение владельца 15.08.2026).
    if (clusterCreateAllowed(model)) {
      setEditPoint(null) // не держим обе формы открытыми одновременно
      setNewPoint(p)
      return
    }
    pointCard.open(p)
  }

  // Клик по существующему маркеру → правка (форма у точки клика).
  const onClusterClick = (info: { id: number; lng: number; lat: number; x: number; y: number }) => {
    setNewPoint(null)
    setEditPoint(info)
  }

  // Клик по живому судну → карточка у судна.
  const onVesselClick = (info: MapPick) => {
    setVesselPick({ lng: info.lng, lat: info.lat, props: info.props })
  }

  // Наведение: читалка. Пока карточка закреплена, курсор её не меняет, но
  // наведённый объект всё равно помним — снимут закрепление, покажем его.
  const onVesselHover = (info: MapPick | null) => setVesselHover(info)
  const onClusterHover = (info: MapPick | null) => setClusterHover(info)
  const onDriftHover = (
    info: { lng: number; lat: number; props: Record<string, unknown> } | null,
  ) =>
    setDriftHover(
      info && {
        lng: info.lng,
        lat: info.lat,
        source: String(info.props.source ?? ''),
        frozen: Boolean(info.props.frozen),
        props: info.props,
      },
    )

  const vesselCard = activeCard(vesselPick, vesselHover)
  const driftCard = activeCard(driftPick, driftHover)

  // Трек найденного судна: живёт НЕ в карточке, а рядом с ней. Карточка
  // закрывается кликом мимо, и трек, привязанный к ней, пришлось бы включать
  // заново после каждого промаха; dropLayers его можно кнопкой в его же легенде.
  const [trackVessel, setTrackVessel] = useState<{ mmsi: string; name: string } | null>(null)
  const vesselTrack = useVesselTrack(mapRef, trackVessel?.mmsi ?? null, date)
  const cardMmsi = vesselCard ? String(vesselCard.card.props.mmsi ?? '') : ''

  // Прогнозный ход — у ВЫБРАННОГО судна, то есть закреплённого кликом. Карточка
  // при наведении транзитна, и рисовать линию под каждым проездом мыши значило
  // бы засыпать кадр прогнозами, которых никто не просил.
  const forecast = useVesselForecast(
    mapRef,
    vesselForecast,
    vesselPick?.props ?? null,
    vesselPick ? { lng: vesselPick.lng, lat: vesselPick.lat } : null,
  )
  const toggleTrack = () => {
    if (!cardMmsi) return
    setTrackVessel(cur =>
      cur?.mmsi === cardMmsi
        ? null
        : {
            mmsi: cardMmsi,
            name: String(vesselCard?.card.props.name ?? '') || cardMmsi,
          },
    )
  }
  // Читалка косяка: гаснет над своей же открытой формой правки и уступает
  // снесённой точке, если та лежит под тем же курсором (как и приоритет клика).
  const hoveredCluster = clusterPeek(
    clusterHover ? { ...clusterHover, id: Number(clusterHover.props.id) } : null,
    editPoint?.id ?? null,
    driftHover != null,
  )
  const peekCluster = hoveredCluster
    ? (clustersData?.clusters ?? []).find(c => c.id === hoveredCluster.id) ?? null
    : null

  // Клик по снесённой точке → её координаты. Формы правки при этом не открываем:
  // правят ОТМЕТКУ, а здесь расчётное положение, редактировать в нём нечего.
  const onDriftClick = (info: {
    lng: number
    lat: number
    x: number
    y: number
    source: string
    frozen: boolean
    role: string
    ref: number
    props: Record<string, unknown>
  }) => {
    // Отметку косяка теперь рисует слой сноса, но ведёт она туда же, куда вела,
    // пока её рисовал слой `clusters`, — в форму правки (см. driftClickTarget).
    if (driftClickTarget(info) === 'cluster-edit') {
      onClusterClick({ id: info.ref, lng: info.lng, lat: info.lat, x: info.x, y: info.y })
      return
    }
    setDriftPick(info)
  }

  // Черновик новой формы: последний вид + дата-время (память 30 мин, см. clusterDraft).
  const draft = useMemo(() => nextDraft(new Date(), loadMemory()), [newPoint])

  // Редактируемый косяк: сама запись (для префилла) + его дрейф (по ref в driftData).
  const editCluster = useMemo(
    () => (editPoint ? clustersData?.clusters.find(c => c.id === editPoint.id) ?? null : null),
    [editPoint, clustersData],
  )
  // Позиция берётся из СРЕЗА на выбранный день, а не из сырой коллекции: там
  // лежит конец трека (последние досчитанные течения), и панель показывала бы
  // послезавтрашнюю точку при ползунке на прошлой неделе.
  const editDrift = useMemo(() => {
    if (!editPoint || !driftForDay) return null
    const d = pickClusterDrift(driftForDay, editPoint.id)
    return d && {
      ...d,
      atLabel: date === today() ? t('models.vessel.now') : t('models.atDate', { date: humanDate(date) }),
    }
  }, [editPoint, driftForDay, date])

  // После создания/правки/удаления точки обновляем и маркеры, и треки дрейфа
  // (бэкенд пересчитал дрейф точки синхронно).
  const refreshClustersAndDrift = () => {
    qc.invalidateQueries({ queryKey: ['cabinet', 'clusters'] })
    qc.invalidateQueries({ queryKey: ['cabinet', 'drift'] })
  }

  const saveEdit = async (body: ClusterInput) => {
    if (!editPoint) return
    await cabinetApi.updateCluster(editPoint.id, body)
    setEditPoint(null)
    refreshClustersAndDrift()
  }

  const deleteEdit = async () => {
    if (!editPoint) return
    await cabinetApi.deleteCluster(editPoint.id)
    setEditPoint(null)
    refreshClustersAndDrift()
  }

  const saveCluster = async (body: ClusterInput) => {
    await cabinetApi.createCluster(body)
    // Запоминаем вид и время для быстрой отметки следующих точек подряд.
    if (body.observed_at) {
      saveMemory({
        speciesId: body.species_id ?? null,
        observedISO: body.observed_at,
        savedAtMs: Date.now(),
      })
    }
    setNewPoint(null)
    refreshClustersAndDrift()
  }

  // Настройки активной модели — ПЛОСКИМ списком, без вложенных складных секций.
  // Гармошка внутри гармошки съедала половину рельса на одни заголовки: чтобы
  // добраться до ползунка окна, приходилось раскрывать «Модели» → «По течениям»
  // → «Дрейф по течениям». Подпись и контрол стоят в одну строку везде, где
  // контрол это позволяет; на своей строке остаются только широкие (флот,
  // глубиномер, ползунок окна).
  const modelBody = (m: ModelKind) => {
    // Дата · глубина · вид переехали в общий блок «Контекст» над разделами —
    // здесь остаются только параметры конкретной модели.
    return (
      <div className="space-y-2.5">
        {/* Флот переехал в общий блок «Наложения» над разделами. */}

        {/* Косяки — и в «Дрейфах», и в «Комфортной среде» (sectionRules). */}
        {schoolsShownIn(m) && (
          <SchoolsControl enabled={schools} onToggle={setSchools} />
        )}

        {m === 'currents' && (
          <DriftControls
            currentsThrough={driftData?.meta.currents_through ?? null}
            status={driftStatus({
              // Раздел «По течениям» = снос; отдельного выключателя больше нет.
              enabled: true,
              speciesSelected: species != null,
              loading: driftLoading,
              data: driftData,
            })}
          />
        )}

        {m === 'hsi' && catchAllowed && (
          <SwitchRow label={t('models.catch.title')} enabled={catchOn} onToggle={setCatchOn} />
        )}

        {m === 'hsi' && hsiSel === NN && (
          <SwitchRow label={t('hsi.nn.marks.title')} enabled={nnMarksOn} onToggle={setNnMarksOn} />
        )}

        {m === 'hsi' && (
          <HsiControl
            catalog={hsiVisible}
            kind={hsiSel}
            onKind={setHsiKind}
          />
        )}

        {m === 'upwelling' && (
          <UpwellingControl
            catalog={upwCatalog}
            present={upwPresentSet}
            raster={upwSel.raster}
            iso={upwSel.iso}
            zero={upwSel.zero}
            onRaster={setUpw}
            onToggleIso={id =>
              setUpwIso(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))}
            onZero={setUpwZero}
          />
        )}

        {/* Изотермы + градиенты переехали в общий блок «Наложения» (единый инструмент). */}
        {m === 'currents' && (
          <HsiDriftControl
            enabled={hsiDrift}
            disabled={species == null}
            onToggle={setHsiDrift}
            count={hsiDriftData?.meta.count ?? null}
            meta={hsiDriftData?.meta ?? null}
            recomputing={!!hsiProgress && !hsiProgress.done}
            downloading={!!historyProg && !historyProg.done}
            animating={cometsRunning}
            reduced={reduced}
            onToggleAnimation={setHsiAnim}
          />
        )}
      </div>
    )
  }

  // Единый рельс: инструменты раздела уходят в общий сайдбар (портал). При свёрнутом
  // рельсе (railSlot === null) — плавающей карточкой поверх карты, как в «Картах».
  // Что писать на плашке скачанной картинки. Название — активная модель:
  // на «Моделях» слоя из каталога нет, а кадр всё равно надо подписать.
  // Шкала растра раздела. До 07.09.2026 её здесь не было вовсе: своя легенда
  // была только у апвеллинга, а течения и «Комфортная среда» красили карту
  // цветом, который нечем прочитать — ни на экране, ни на скачанном кадре.
  //
  // Механизмы апвеллинга краёв НЕ спрашивают: у них своя палитра с готовыми
  // опорами, и `min`/`max` здесь не «неизвестны», а не нужны — разница
  // существенная, см. `scaleRow.ScaleInput`.
  const scaleLayerId = seriesOverride
    ? scaleLayerOf(seriesOverride)
    : model === 'currents' ? 'currents'
      : model === 'hsi' ? hsiSel
        : upwSel.raster
  const scaleSpec = useLayerSpec(scaleLayerId)
  // Границы спрашиваются у слоёв общей пирамиды. У механизмов апвеллинга своя
  // палитра с готовыми опорами, и запрашивать их края значило бы получить
  // «не знаем» там, где шкала уже известна.
  // Нейрокарта — туда же: её шкала ИЗМЕРЕНА один раз по обучающему периоду
  // (36 дат, 1 026 936 клеток) и стоит опорами палитры. `/cabinet/legend`
  // отвечает ей `null/null` — она артефакт, а не слой общей пирамиды, — и
  // спросив края, мы получили бы «не знаем» там, где шкала известна.
  const scaleAsksBounds = !scaleLayerId.startsWith('upw_')
    && scaleLayerId !== 'upwelling' && scaleLayerId !== NN
  // Шкала у каждого района своя — подписываем ту, что под центром кадра.
  const scaleRegion = regionAt(
    regionScope.regions, mapView.center?.[0] ?? null, mapView.center?.[1] ?? null)
  const { data: scaleBounds } = useQuery({
    queryKey: ['cabinet', 'legend', scaleLayerId, date, settledDepth, scaleRegion],
    queryFn: () => cabinetApi.legend({
      layer: scaleLayerId, date, depth: settledDepth, region: scaleRegion,
    }),
    enabled: scaleAsksBounds && Boolean(scaleSpec),
  })
  const modelScale = scaleSpec
    ? {
        layerId: scaleSpec.id,
        unit: scaleSpec.unit,
        palette: scaleSpec.palette,
        ...(scaleAsksBounds
          ? { min: scaleBounds?.min ?? null, max: scaleBounds?.max ?? null }
          : {}),
      }
    : null

  // Подпись кадра называет ПОДРАЗДЕЛ, а не раздел: обе карты «Комфортной среды»
  // подписались бы одинаково, и в переписке их стало бы не различить.
  const modelLabelKey = model === 'hsi' && hsiCatalog.length > 1
    ? `layers.${hsiSel}`
    : MODELS.find(m => m.id === model)?.labelKey
  // Под серией подпись — у КАДРА: иначе карта зоопланктона уехала бы в архив
  // подписанной «Дрейфы», и файл параметров спорил бы с картинкой.
  const exportLabelKey = seriesOverride?.layerLabelKey ?? modelLabelKey ?? 'models.pickerTitle'
  const exportInfo = useExportInfo(t(exportLabelKey), {
    scale: modelScale,
    earthquakes: earthquakeCount,
    waveSource,
    drift: driftVisible,
    driftAnonymized: catchAnonymized,
    driftCatchHaloHidden: catchHaloHidden,
    sstData,
    sstPartial,
    waveData,
    surfaceData,
    drawn: { isotherm: isothermDrawn, isothermSurface: surfaceDrawn,
             fronts: frontsDrawn, isobaths: isobathsDrawn, waves: waveDrawn,
             ...linesDrawn },
    // Механизм называем ТОЛЬКО в своём разделе: в кадре течений пояснение про
    // подъём воды объясняло бы слой, которого на картинке нет.
    upwellingMechanism: model === 'upwelling' ? upwSel.raster : undefined,
  }, seriesOverride)

  // Живой снимок для серии: объект-исполнитель создаётся на каждом рендере, а
  // цикл держит тот, что был на клике. Через ref он читает СВЕЖИЕ значения —
  // тот же приём, что у `layerRef` в записи ролика.
  const seriesLiveRef = useRef({ info: exportInfo, pending: false })
  seriesLiveRef.current = {
    info: exportInfo,
    // Изолинии механизмов входят сюда наравне с изотермами: кадр, снятый до
    // их приезда, — это картинка, о которой потом не скажешь, чего на ней не
    // хватает (то же правило, что у `toolPending`).
    pending: tempPending || wavePending || isobathPending || upwIsoPending,
  }

  // Запись ролика ведёт анимацию сама: ролик идёт ПО СУТКАМ окна перемотки, а
  // фаза кометы считается от номера кадра — шов цикла нулевой по построению.
  // Свободный такт на это время замолкает (recordingRef), экран после записи
  // возвращается в исходное состояние.
  //
  // См. docs/plans/2026-08-16-video-drift-timeline-design.md
  const recordWinRef = useRef<RecordWindow | null>(null)
  const recordDayRef = useRef<number | null>(null)


  /** Что за растр показывать на эту дату.
   *
   *  `null` — у модели растра нет ИЛИ на карте его сейчас не показано. Второе
   *  важнее первого: чистая карта — сознательное состояние (прогноз без вида,
   *  снятый слой), и запись, подрисовавшая туда растр, показала бы человеку не
   *  тот кадр, который он видел, когда жал кнопку. */
  const rasterUrlFor = (day: string): string | null => {
    if (!layerRef.current) return null
    const bg = backgroundLayerFor(model, sectionSub)
    if (bg) return backgroundTileUrl(bg, day, settledDepth, null, palette, species)
    if (model === 'hsi' && species != null) return probabilityTileUrl(species, day, settledDepth)
    return null
  }

  /** Готовность слоя на одну дату: тайлы уже есть или их можно отрисовать.
   *
   *  Спрашиваем разное у разных моделей: у фоновых слоёв — `availability`, у
   *  probability — статус растра (availability отвечает им «не готово» по
   *  определению). Модель без растра готова всегда: синхронизировать нечего. */
  const probeDay = async (day: string): Promise<{ tilesReady: boolean; dataPresent: boolean }> => {
    const map = mapRef.current
    // Растра на карте нет — синхронизировать нечего, и ролик про снос не должен
    // упираться в готовность слоя, которого в кадре не будет.
    if (!layerRef.current) return { tilesReady: true, dataPresent: true }
    const bg = backgroundLayerFor(model, sectionSub)
    if (bg && map) {
      const av = await cabinetApi.availability({
        layer: bg, date: day, depth: settledDepth, bbox: map.getBBox(),
        species_id: species,
      })
      return {
        tilesReady: av.is_static || av.tiles_ready,
        dataPresent: av.data_present || av.tiles_ready || av.is_static,
      }
    }
    if (model === 'hsi' && species != null) {
      const status = await cabinetApi.rasterStatus(species, day)
      return {
        tilesReady: probabilityAutoShow(status, settledDepth),
        // Поле посчитано — тайлы этой глубины дорисовываются лениво, это секунды.
        dataPresent: status?.status === 'ready',
      }
    }
    return { tilesReady: true, dataPresent: true }
  }

  // Готовность того, что на карте, — для кнопок скачивания.
  //
  // Кадр без данных наружу не отдаём: проверить картинку нечем, а пустое поле
  // на ней неотличимо от честного «здесь ничего нет». Спрашиваем тем же
  // `probeDay`, что и запись ролика: второй способ узнать то же самое рано или
  // поздно ответил бы иначе.
  const [exportLayer, setExportLayer] = useState<LayerShowState>('none')
  useEffect(() => {
    // Серия и запись сами ведут слой — их промежуточные состояния не про кнопку.
    if (seriesRef.current || recordingRef.current) return
    let alive = true
    void (async () => {
      // Растра на карте нет — кадр без полей, и пропускам взяться неоткуда.
      if (!layerRef.current) {
        if (alive) setExportLayer('none')
        return
      }
      try {
        const a = await probeDay(date)
        if (alive) setExportLayer(layerStateOf(a.dataPresent || a.tilesReady, !a.tilesReady))
      } catch {
        // Спросить не смогли — не доказали. Молчание не повод отдать кадр.
        if (alive) setExportLayer('no-data')
      }
    })()
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, sectionSub, date, settledDepth, species, loadNonce])

  const exportAnimation: ExportAnimation = {
    // Снос без комет — тоже движение: ролик про косяки осмыслен и без усиков.
    // Флот — третий самостоятельный источник движения: ролик, где по неделе
    // истории едут только суда, отвечает на свой вопрос и без косяков.
    ready: comets.recordable || driftVisible || fleet.length > 0,
    begin: async onProgress => {
      recordingRef.current = true
      // Вид записи — тот же, что у анимации: статичные усики уходят с карты
      // (`recording` входит в cometMode), иначе кометы легли бы поверх них.
      setRecording(true)
      if (hsiDriftData) cometTrailsRef.current = prepareTrails(hsiDriftData)
      // Даём React закоммитить состояние, а карте — перерисоваться без статики.
      await new Promise(res => setTimeout(res, 0))
      await new Promise(res => requestAnimationFrame(() => res(null)))
      const map = mapRef.current?.getMap()
      if (map) await waitMapIdle(map)

      const now = today()
      const current = dayOffset(date, now)
      const offsets: number[] = []
      const span = dayRange(domainLimits.period)
      for (let d = span.min; d <= span.max; d++) offsets.push(d)
      // Опрос готовности — read-only, поэтому все даты спрашиваем разом. Работу
      // ставим уже по одной (см. renderDates).
      const probes = await Promise.all(
        offsets.map(async offset => {
          const day = dateFromOffset(offset, now)
          try {
            return { offset, date: day, ...(await probeDay(day)) }
          } catch {
            // Сеть моргнула — считаем дату неготовой: домысленная готовность
            // обернулась бы пустым кадром посреди ролика.
            return { offset, date: day, tilesReady: false, dataPresent: false }
          }
        }),
      )

      const bbox = mapRef.current?.getBBox()
      const pending = datesToRender(probes, current)
      const rendered = bbox
        ? await renderDates(
            pending.map(p => ({
              date: p.date,
              bbox,
              layers: [backgroundLayerFor(model, sectionSub) ?? 'probability'],
              depth: settledDepth,
              species_id: model === 'hsi' ? species : null,
              kind: model === 'hsi' ? ('model' as const)
                : model === 'upwelling' ? ('upwelling' as const)
                : ('raw' as const),
            })),
            {
              prepare: body => cabinetApi.prepare(body as never),
              confirm: id => cabinetApi.confirm(id),
              progress: id => cabinetApi.progress(id),
              onProgress,
            },
          )
        : new Set<string>()

      const win = recordWindow(
        probes.map(p => ({ offset: p.offset, ok: p.tilesReady || rendered.has(p.date) })),
        current,
      )
      recordWinRef.current = win
      recordDayRef.current = null
      fleetHistoryRef.current = null
      fleetMetaRef.current = null

      // История флота — ПОСЛЕ выбора окна: тянуть сутки, которые в файл не
      // попадут, незачем. Один запрос на всю запись; спрашивать его на каждом
      // из полутора сотен кадров растянуло бы запись на минуты.
      const viewBox = mapRef.current?.getBBox()
      if (win && viewBox && fleet.length > 0) {
        const edges = windowLabelRange(win, now)
        try {
          const parts = await Promise.all(
            fleet.map(seg =>
              cabinetApi
                .fleetVesselsHistory({
                  scope: backendScopeFor(seg),
                  from: edges.from,
                  to: edges.to,
                  speciesId: species,
                  includeUncategorized: showUncategorized,
                  bbox: viewBox,
                  flags: backendScopeFor(seg) === 'foreign' ? flags : undefined,
                })
                .catch(() => null),
            ),
          )
          const merged = mergeFleetHistory(parts)
          fleetHistoryRef.current = merged
          fleetMetaRef.current = merged.meta
        } catch {
          // Отказ сети не срывает запись: суда просто останутся стоять, а
          // ролик про снос и растр осмыслен и без их движения.
          fleetHistoryRef.current = null
        }
      }

      // Пустая раскладка — «готовых суток нет»: отдельный исход, не отказ
      // кодировщика (см. exportVideo.exportNote).
      return win ? framePlan(win.from, win.to) : []
    },
    timerFor: spec => {
      const win = recordWinRef.current
      if (!win) return null
      const now = today()
      const edges = windowLabelRange(win, now)
      // Час ставим ТОЛЬКО когда в кадре есть то, что меняется внутри суток:
      // усик считается по четырём срезам поля, если оно шестичасовое. Усиков
      // нет — `hsiDriftData` пуст, и подпись честно остаётся датой: растр
      // существует по суткам, а точки сноса внутри суток интерполируются.
      return {
        date: dateFromOffset(spec.dayOffset, now),
        hour: sliceHour(spec.frac, hsiDriftData?.meta.currents_step_h ?? 24),
        from: edges.from,
        to: edges.to,
        today: now,
      }
    },
    renderFrame: async spec => {
      const handle = mapRef.current
      const map = handle?.getMap()
      if (!handle || !map) return
      const now = today()
      const day = dateFromOffset(spec.dayOffset, now)

      // Растр меняем ТОЛЬКО на смене суток: он существует по датам, и трогать
      // его на каждом кадре значило бы восемнадцать раз перезапросить те же тайлы.
      const dayChanged = recordDayRef.current !== spec.dayOffset
      if (dayChanged) {
        recordDayRef.current = spec.dayOffset
        const url = rasterUrlFor(day)
        if (url) handle.refreshLayer(url)
      }

      // Точки выловов и косяков — на дробный день: положение и ореол погрешности
      // интерполируются между посчитанными сутками (см. sliceDriftCollection).
      const src = driftForSection(applySchoolsVisibility(driftData ?? null, schools), model, catchShown)
      if (driftEnabled && src) {
        handle.setDrift('drift', sliceDriftCollection(src, day, now, spec.frac))
      }

      // Суда — на момент кадра. Час здесь настоящий, в отличие от доли суток у
      // сноса: АИС живёт в реальном времени, а течений мельче суток не бывает.
      const hist = fleetHistoryRef.current
      if (hist) handle.setVessels('vessels', sliceFleetAt(hist, frameTimeMs(spec, now)))

      // То же условие, по которому на время записи снята статика (cometMode).
      if (comets.recordable) {
        handle.setHsiComets('currents', buildCometFrame(cometTrailsRef.current, spec.phase))
      }
      // На смене суток ждём дольше: приезжают новые тайлы. Внутри суток кадров
      // восемнадцать, и полсекунды на каждый превратили бы запись в минуты.
      await waitMapIdle(map, dayChanged ? 1200 : 150)
    },
    end: () => {
      recordingRef.current = false
      // Гашение флага и возвращает флот на живые позиции: хук слоя перезапустит
      // эффект и перечитает их сам (см. useFleetLayer, проп `paused`).
      setRecording(false)
      fleetHistoryRef.current = null
      recordDayRef.current = null
      // Возвращаем экран на выбранную дату: растр и снос ушли в конец окна.
      const handle = mapRef.current
      const url = rasterUrlFor(date)
      if (handle && url) handle.refreshLayer(url)
      if (handle && driftForDay) handle.setDrift('drift', driftForDay)
    },
    coverageLabel: () => {
      const win = recordWinRef.current
      if (!win) return null
      const edges = windowLabelRange(win, today())
      return [
        t('map.export.covered', { from: humanDate(edges.from), to: humanDate(edges.to) }),
        // Про флот молчать нельзя: историю за окно имеет не весь флот кадра, и
        // без этой строки неподвижные суда читались бы как поломка записи, а не
        // как «эти молчали». По той же причине называется и обрезка потолком.
        ...fleetCoverageNotes(fleetMetaRef.current).map(m => t(m.key, m.vars)),
      ].join(' · ')
    },
  }

  /** Адрес растра кадра серии. Кадр показывается ПОДМЕНОЙ растра, а не сменой
   *  раздела: раздел тянет за собой три асинхронных эффекта, которые
   *  перезапустились бы посреди серии. */
  const seriesLayerUrl = (frame: SeriesFrame): string | null => {
    if (frame.layer === 'bathymetry') return bathymetryTileUrl()
    if (frame.layer === 'probability') {
      return species == null ? null : probabilityTileUrl(species, date, frame.depth ?? 0)
    }
    // Остальное — той же дорогой, что и фон раздела (`backgroundTileUrl`):
    // замороженный индекс, семейство механизмов и общая пирамида собираются
    // там, и вторая сборка адреса разъехалась бы с первой молча.
    return backgroundTileUrl(frame.layer, date, frame.depth ?? 0, null, palette, species)
  }

  /** Дать React закоммитить состояние, а карте — перерисоваться. Тот же приём,
   *  что в начале записи ролика. */
  const commitFrame = async () => {
    await new Promise(res => setTimeout(res, 0))
    await new Promise(res => requestAnimationFrame(() => res(null)))
  }

  // Состав серии режется каталогом кабинета: слой, которого кабинету не выдали,
  // в архив не попадает. Иначе кнопка стала бы обходом гейта — растровые тайлы
  // среды отдаются без авторизации, и карта зоопланктона нарисовалась бы там,
  // где среду не покупали (см. map/seriesCabinet.ts).
  // Состав считается из КАТАЛОГА кабинета (владелец 07.09.2026: «абсолютно все
  // картинки должны попадать в серию»), и он же — единственный источник прав:
  // слой, которого кабинету не выдали, в каталог не приезжает и кадра не даёт.
  // `framesForCabinet` поверх этого снимает инструменты над полем температуры у
  // кабинета без гидрологии — сетку SST ему не отдадут, и включённый тумблер
  // врал бы дважды.
  const seriesFrames = framesForCabinet(
    composeSeries({
      layers: layerCatalog?.layers ?? [],
      speciesId: species,
      depth: settledDepth,
    }),
    {
      layerIds: (layerCatalog?.layers ?? []).map(l => l.id),
      hydrology: hydrologyOf(layerCatalog),
    },
  )
  const exportSeriesRunner: SeriesRunner = {
    // Кнопка больше НЕ зависит от вида: кадры среды видонезависимы, и гасить
    // из-за отсутствия вида всю серию незачем. Пустой каталог (ещё не приехал
    // либо кабинету не выдали ничего) — единственная причина молчания.
    ready: seriesFrames.length > 0,
    reason: seriesFrames.length > 0 ? 'ok' : 'no-series',
    frames: seriesFrames,

    begin: async () => {
      seriesRef.current = true
      // Что было на карте до серии — туда и вернёмся: кадры меняют растр, и
      // «просто перерисовать текущую модель» вернуло бы не тот слой, который
      // человек оставил (он мог снять его вовсе).
      seriesRestoreRef.current = layerRef.current
      await commitFrame()
    },

    // Разведка: что со слоем кадра. Карту не трогает и ничего не заказывает —
    // её ответы wanted, чтобы задать человеку ОДИН вопрос со всей картиной.
    probe: async (frame): Promise<FrameVerdict> => {
      const probe = probeFor(frame, species)
      // Батиметрия статична: поле у неё есть всегда, а плитки рисуются лениво
      // по первому запросу — их дожидается сам кадр (см. waitTilesReady).
      if (probe.kind === 'static') return 'ready'
      if (probe.kind === 'none') return 'unknown'
      const bbox = mapRef.current?.getBBox()
      try {
        if (probe.kind === 'raster') {
          const status = await cabinetApi.rasterStatus(probe.speciesId, date)
          if (probabilityAutoShow(status, probe.depth)) return 'ready'
          return status?.status === 'ready' ? 'needs-render' : 'no-data'
        }
        if (!bbox) return 'unknown'
        const av = await cabinetApi.availability({
          layer: probe.layer, date, depth: probe.depth, bbox,
        })
        if (av.is_static || av.tiles_ready) return 'ready'
        return av.data_present ? 'needs-render' : 'no-data'
      } catch {
        // Молчание — не готовность: доказательства данных у нас нет, и приняв
        // его за «да», мы отдали бы картинку без данных.
        return 'unknown'
      }
    },

    // Дорисовка тайлов кадра. Зовётся ТОЛЬКО для кадров, у которых поле есть:
    // полный пайплайн стоит 44 минуты на дату, и кнопка не может его заказать.
    render: async (frame, onProgress) => {
      const bbox = mapRef.current?.getBBox()
      const request = bbox ? renderRequestFor(frame, { date, bbox, speciesId: species }) : null
      if (!request) return false
      try {
        const done = await renderDates([request], {
          prepare: body => cabinetApi.prepare(body as never),
          confirm: id => cabinetApi.confirm(id),
          progress: id => cabinetApi.progress(id),
          onProgress,
        }, SERIES_RENDER_ATTEMPTS)
        return done.has(request.date)
      } catch {
        return false
      }
    },

    apply: async frame => {
      const started = Date.now()
      const tools = frameOverride(frame, { depth: settledDepth })
      setSeriesOverride(tools)
      await commitFrame()
      const url = seriesLayerUrl(frame)
      if (url) showLayer(url, model, 'refresh')
      else clearLayer()
      // Дать MapLibre сбросить кэш прежних плиток ДО первого вопроса о
      // готовности: спрошенный вплотную, он ответил бы «всё на месте» про
      // предыдущий слой.
      await commitFrame()
      // Ждём УСЛОВИЕ, а не таймер: сетка температуры едет с дебаунсом плюс
      // сеть, и пауза «на глазок» иногда клала бы в архив карту без изотермы.
      const inTime = await waitFor(() => !seriesLiveRef.current.pending, {
        timeoutMs: SERIES_TOOL_TIMEOUT_MS,
        pollMs: 100,
      })
      const map = mapRef.current?.getMap()
      // Плитки — тем же правилом: снимаем то, что НА карте, а не то, что
      // заказано. Готовность тайлов на сервере (`prepare`) и приезд их в
      // браузер — разные события, и второе бывает много дольше первого.
      const tilesInTime = await waitTilesReady(map, {
        timeoutMs: SERIES_TILES_TIMEOUT_MS,
        pollMs: 200,
      })
      if (map) await waitMapIdle(map, SERIES_IDLE_MS)
      return {
        waitedMs: Date.now() - started,
        toolsTimedOut: !inTime,
        tilesTimedOut: !tilesInTime,
        tools,
        legend: seriesLiveRef.current.info.legend ?? [],
      }
    },

    info: () => seriesLiveRef.current.info,

    // Данные кадра одним файлом. Собирает сервер: поля лежат в его базе, а в
    // браузере нет ни кодировщика NetCDF, ни самих значений на всех горизонтах.
    // Рамку берём ТУ ЖЕ, что и картинки, — иначе файл описывал бы другой кадр.
    dataset: async () => {
      const bbox = mapRef.current?.getBBox()
      if (!bbox) return null
      return cabinetApi.exportGrid({ date, bbox, species })
    },

    header: () => {
      const map = mapRef.current?.getMap()
      const canvas = map?.getCanvas()
      const center = map?.getCenter()
      return {
        brandName: exportInfo.brand.name,
        caption: exportInfo.caption,
        speciesLabel: exportInfo.speciesLabel,
        speciesId: species,
        date,
        today: today(),
        center: { lng: center?.lng ?? 0, lat: center?.lat ?? 0 },
        zoom: map?.getZoom() ?? 0,
        bbox: mapRef.current?.getBBox() ?? { lat_min: 0, lat_max: 0, lon_min: 0, lon_max: 0 },
        frameSize: { width: canvas?.width ?? 0, height: canvas?.height ?? 0 },
        palette: palette ?? null,
        generatedAt: new Date(),
      }
    },

    end: () => {
      seriesRef.current = false
      setSeriesOverride(null)
      const saved = seriesRestoreRef.current
      seriesRestoreRef.current = null
      // Возвращаем ровно тот растр, что был: серия меняла его сама, и
      // «перерисовать текущую модель» вернуло бы не то, что человек оставил.
      if (saved) showLayer(saved.url, saved.owner, 'refresh')
      else clearLayer()
    },
  }

  // Сохранить вид раздела. Набор ключей у «Моделей» свой (модель, окно
  // свежести, траектории HSI, косяки), поэтому вид помечается разделом — иначе
  // ссылка увела бы в «Карты», растеряв всё это по дороге.
  const saveCurrentView = async () => {
    const name = window.prompt(t('maps.savedView.prompt'))
    if (!name) return
    await cabinetApi.createSavedView(name, saveViewState(
      'models',
      {
        model, species, depth, date,
        overlays, waves: waveOverlay, showUncat: showUncategorized, waveStep, flags,
        isobath: isobathStep, isotherm, isothermSurface, isothermSpread,
        fronts, frontsUnit, hsiDrift, schools, vesselForecast,
        fleet: serializeFleet(fleet),
        zoom: mapRef.current?.getMap()?.getZoom(),
      },
      mapRef.current?.getBBox(),
      today(),
    ))
  }

  const tools = (
    <div className="space-y-4">
      <ModelAccordion
        active={model}
        onSelect={setModel}
        body={modelBody}
        allowed={m => sectionAllowed(m, history)}
      />
      {/* Скачать текущий вид: PNG кадра и MP4 анимации усиков. Анимацию ведёт
          сама запись (точные фазы цикла), экран после неё возвращается. */}
      <ExportViewButton
        mapRef={mapRef}
        info={exportInfo}
        animation={exportAnimation}
        series={exportSeriesRunner}
        layerState={exportLayer}
      />
      <button
        type="button"
        onClick={saveCurrentView}
        className="flex w-full items-center justify-center gap-2 rounded-lg border border-hull px-3 py-2 text-sm text-mist transition-colors hover:border-sonar hover:text-foam"
      >
        <Icon name="ic-save-view" className="h-4 w-4" /> {t('maps.savedView.save')}
      </button>
    </div>
  )

  return (
    <div className="relative h-full w-full">
      {railSlot ? (
        createPortal(
          <div className="pb-2 pt-1">{tools}</div>,
          railSlot,
        )
      ) : (
        <MapPanel storageKey="models.panel" className="w-64" surface="bg-deep">
          {/* Свёрнутый рельс: блоки «Контекст» и «Наложения» — плавающей карточкой. */}
          <div className="px-3 pb-3">
            <ContextBlock />
            <OverlaysBlock />
            {tools}
          </div>
        </MapPanel>
      )}

      <div className="relative h-full w-full">
        {/* Вид входа: ссылка и память раздела сильнее рамки района, а пока набор
            районов не приехал — карту не строим. Построить на зашитом центре и
            перелететь значило бы показать на домене партнёра чужой океан. */}
        {openWith && (
          <MapCanvas
            ref={mapRef}
            onPointClick={onMapClick}
            onClusterClick={onClusterClick}
            onDriftClick={onDriftClick}
            onVesselClick={onVesselClick}
            onVesselHover={onVesselHover}
            onClusterHover={onClusterHover}
            onDriftHover={onDriftHover}
            onEezHover={eezCard.handleHover}
            onEezClick={eezCard.handleClick}
            onQuakeHover={quakeCard.handleHover}
            onQuakeClick={quakeCard.handleClick}
            center={
              openWith.lng != null && openWith.lat != null
                ? [openWith.lng, openWith.lat]
                : undefined
            }
            zoom={openWith.zoom ?? undefined}
            onMoveEnd={mapView.onMoveEnd}
          />
        )}
        {regionScope.forbidden && <RegionForbiddenPlate />}
        <div className="pointer-events-none absolute inset-0">
          {/* Единая легенда активных наложений — одна плашка справа снизу. */}
          <div className="absolute bottom-24 right-4">
            <MapLegendPanel
              overlays={shownOverlays}
              earthquakes={earthquakeCount}
              waveSource={waveSource}
              sstData={sstData}
              sstPartial={sstPartial}
              waveData={waveData}
              eezAttribution={eezMeta.attribution}
              surfaceData={surfaceData}
              drawn={{ isotherm: isothermDrawn, isothermSurface: surfaceDrawn,
                       fronts: frontsDrawn, isobaths: isobathsDrawn, waves: waveDrawn,
             ...linesDrawn }}
            />
          </div>
          {driftVisible && (
            <div className="absolute bottom-24 left-4">
              <CollapsibleLegend title={t('models.drift.legendTab')}>
                <DriftLegend
                  anonymized={catchAnonymized}
                  catchHaloHidden={catchHaloHidden}
                />
              </CollapsibleLegend>
            </div>
          )}
          {trackVessel && (
            <div className="absolute right-4 top-4">
              <CollapsibleLegend title={t('models.vesselTrack.legendTab')}>
                <VesselTrackLegend
                  name={trackVessel.name}
                  from={vesselTrack.range.from}
                  to={vesselTrack.range.to}
                  hours={vesselTrack.meta?.hours}
                  onHide={() => setTrackVessel(null)}
                />
              </CollapsibleLegend>
            </div>
          )}
          {showsDayTimeline(model) && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
              <DayTimeline
                date={date}
                today={today()}
                onChange={setDate}
                // Оба про течения: в «Комфортной среде» стрелок нет, и граница
                // «течения до…» говорила бы о слое, которого на карте нет.
                currentsThrough={model === 'currents' ? (driftData?.meta.currents_through ?? null) : null}
                notice={model === 'currents' ? arrowsNotice(arrowsState) : null}
              />
            </div>
          )}
          {/* Шкала растра — там же, где легенда апвеллинга. У самого апвеллинга
              своя легенда богаче (изобаты, стрелка, семейства), и вторая шкала
              рядом с ней была бы повтором. */}
          {model !== 'upwelling' && (
            <div className="absolute bottom-24 left-4">
              <ScalePanel scale={modelScale} />
            </div>
          )}
          {model === 'upwelling' && (
            <div className="absolute bottom-24 left-4">
              <UpwellingLegend
                mechanism={upwSel.raster}
                iso={upwSel.iso}
                depth={upwDepthApplicable(upwSel, upwCatalog) ? nearestMechDepth(depth) : null}
              />
            </div>
          )}
          {/* Короткая ссылка на этот вид — в самом углу, под легендами моделей. */}
          <div className="absolute bottom-4 left-4">
            <ShareLinkButton />
          </div>
          {model === 'upwelling' && (isobathsFailed || arrowsNotice(arrowsState)) && (
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 rounded-lg border border-hull bg-abyss/90 px-3 py-1.5 text-xs text-mist shadow-console backdrop-blur">
              {isobathsFailed ? t('map.isobaths.failed') : arrowsNotice(arrowsState)}
            </div>
          )}
          <div
            className={
              'absolute left-1/2 -translate-x-1/2 ' +
              // Над ползунком дней, где он есть — иначе кнопка легла бы на него.
              (showsDayTimeline(model) ? 'bottom-24' : 'bottom-6')
            }
          >
            {canLoad ? (
              <LoadDataButton
                state={state}
                progress={progress}
                cardProgress={cardProgress}
                hsiProgress={hsiProgress}
                hsiReason={hsiDriftEnabled ? hsiDriftData?.meta.reason ?? null : null}
                historyProgress={historyProg}
                error={error}
                onClick={handleLoad}
              />
            ) : nnFill ? (
              // У нейрокарты кнопки нет; плашка нужна только прогнозу на +1/+2.
              (nnMarks?.provenance?.lead_days ?? 0) > 0 && (
                <div className="pointer-events-auto rounded-lg border border-hull bg-abyss/90 px-4 py-2 text-sm text-sonar shadow-console backdrop-blur">
                  {t('hsi.nn.lead', { n: nnMarks!.provenance!.lead_days! })}
                </div>
              )
            ) : (
              <div className="pointer-events-auto rounded-lg border border-hull bg-abyss/90 px-4 py-2 text-sm text-mist shadow-console backdrop-blur">
                {t('models.species.pickShort')}
              </div>
            )}
          </div>
        </div>
        {/* Карточка живёт, только пока её слой на карте: слой убрали (сменили
            модель, выключили флот) — mouseleave под курсором уже не придёт, и
            карточка висела бы над картой без своего объекта. */}
        {driftCard && driftEnabled && (
          <TrackPointCard
            // Ключа НЕТ намеренно (см. cardPick.ts): с ключом по пикселям курсора
            // узел пересоздавался на каждое дрожание мыши, и не доехавшие сносы
            // копились карточками над картой. Один узел, обновление на месте.
            lat={driftCard.card.lat}
            lon={driftCard.card.lng}
            date={date}
            today={today()}
            frozen={driftCard.card.frozen}
            static={driftCard.card.props?.status === 'static'}
            source={driftCard.card.source}
            props={driftCard.card.props}
            pinned={driftCard.pinned}
            onClose={() => setDriftPick(null)}
          />
        )}
        {peekCluster && hoveredCluster && schoolsShownIn(model) && (
          <ClusterPeek
            key={`peek-${peekCluster.id}`}
            cluster={peekCluster}
            species={speciesList}
          />
        )}
        {/* Отсчёт: карточка встаёт у клика по воде и держится до крестика.
            Независима от карточек объектов, как и они между собой. */}
        {pointCard.pick && (
          <PointCard
            anchor={pointCard.pick}
            probe={pointCard.probe}
            loading={pointCard.loading}
            failed={pointCard.failed}
            onClose={pointCard.close}
            substrate={pointSubstrate}
          />
        )}
        {/* Рубеж ИЭЗ: читалка по наведению, закреплённая по клику. Без неё
            линию на карте нечем назвать — чей это рубеж и на каком основании
            он проведён, приходилось спрашивать у базы. */}
        {eezCard.shown && (
          <EezCard
            anchor={eezCard.shown.hit}
            props={eezCard.shown.hit.props}
            pinned={eezCard.shown.pinned}
            onClose={eezCard.close}
          />
        )}
        {/* Землетрясение: читалка по наведению, закреплённая по клику. Ключа
            нет по той же причине, что у TrackPointCard (см. cardPick.ts). */}
        {quakeCard.shown && (
          <EarthquakeCard
            anchor={quakeCard.shown.hit}
            props={quakeCard.shown.hit.props}
            pinned={quakeCard.shown.pinned}
            onClose={quakeCard.close}
          />
        )}
        {vesselCard && fleet.length > 0 && (
          <VesselCard
            // Ключа нет по той же причине, что у TrackPointCard (см. cardPick.ts).
            anchor={vesselCard.card}
            props={vesselCard.card.props}
            pinned={vesselCard.pinned}
            onClose={() => setVesselPick(null)}
            track={{
              visible: trackVessel?.mmsi === cardMmsi,
              busy: vesselTrack.busy && trackVessel?.mmsi === cardMmsi,
              hint:
                trackVessel?.mmsi === cardMmsi && vesselTrack.meta
                  ? trackHint(vesselTrack.meta)
                  : null,
              onToggle: toggleTrack,
            }}
            forecast={{
              visible: vesselForecast,
              hint: forecastHint(forecast),
              onToggle: () => setVesselForecast(v => !v),
            }}
            // Позиция судна поехала — слой обязан её показать. Вьюпорт при этом
            // тот же, и без счётчика слой не узнал бы, что данные изменились.
            onRefreshed={() => setFleetNonce(n => n + 1)}
          />
        )}
        {newPoint && model === 'currents' && (
          <ClusterForm
            key={`${newPoint.x},${newPoint.y}`}
            point={newPoint}
            species={speciesList}
            initialSpeciesId={draft.speciesId}
            initialObservedAt={draft.observedAt}
            onSubmit={saveCluster}
            onCancel={() => setNewPoint(null)}
          />
        )}
        {editPoint && editCluster && schoolsShownIn(model) && (
          <ClusterForm
            key={`edit-${editPoint.id}`}
            point={editPoint}
            species={speciesList}
            editing
            initialSpeciesId={editCluster.species_id}
            initialObservedAt={editCluster.observed_at ? new Date(editCluster.observed_at) : new Date()}
            initialSize={editCluster.size_class}
            initialNote={editCluster.note ?? ''}
            drift={editDrift}
            onSubmit={saveEdit}
            onCancel={() => setEditPoint(null)}
            onDelete={deleteEdit}
            // Право на удаление считает сервер (автор отметки или владелец кабинета).
            // Старые записи приходят без поля — тогда кнопку не прячем, отказ даст сервер.
            canDelete={editCluster.can_delete ?? true}
          />
        )}
        {state === 'manifest' && manifest && (
          <ManifestModal
            manifest={manifest}
            onConfirm={confirm}
            // Отказ в модалке — это отказ от всей работы клика, включая докачку
            // истории течений: без снятия замка она ушла бы в очередь на первом
            // же ответе слоя после возврата в 'idle'.
            onCancel={() => {
              historyArmedRef.current = false
              hsiArmedRef.current = false
              cancel()
            }}
          />
        )}
      </div>
    </div>
  )
}
